import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { trigger, transition, style, animate, query, stagger, state } from '@angular/animations';
import { WorkflowDataService } from '../../services/workflow-data.service';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Chart, { TooltipItem } from 'chart.js/auto';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { DueQuotationDialogComponent } from '../due-quotation-dialog/due-quotation-dialog.component';

Chart.register(ChartDataLabels);
interface QuotationItem {
  orderId: string;  // Unique order identifier
  uuid?: string;    // Optional UUID
  quotationId: string;
  quotationNo: string;
  customerName: string;
  project: string;  // Renamed from 'projectName' for consistency
  quotationDate: string;
  totalPrice: number;  // Total price (e.g., sum of invoices or quoted amount)
  poNumber: string;
  poDate: string;
  currentStep?: 'quotation' | 'po' | 'pi' | 'workOrder' | 'ci' | 'payment';  // Workflow step
  completed?: boolean;  // Workflow completion status
  invoices: {  // Array for multiple invoices
    invoiceNo: string;
    date: string;  // Renamed from 'ciDate' to be generic per invoice
    invoicePrice: number;
  }[];
  payments: {  // Array for multiple payments
    invoiceNo: string;  // Associated invoice
    paymentPrice: number;
    paymentReceived: boolean;
    invoiceDate: string;
    paymentReceivedDate: string;
  }[];
  paymentDueDate: string;  // Due date for payment
  country: string;
  email: string;

  // Optional computed fields (can be calculated in component)
  totalInvoiceAmount?: number;  // Sum of all invoicePrice
  totalPaymentAmount?: number;  // Sum of all paymentPrice
  outstandingAmount?: number;   // totalInvoiceAmount - totalPaymentAmount
}

interface ChartData {
  labels: string[];
  datasets: any[];
}

interface YearOption {
  name: string;
  value: { start: Date; end: Date };
}

interface TopCustomer {
  name: string;
  value: number;
}

interface MonthData {
  month: string;
  value: number;
}

@Component({
  selector: 'app-quotation-dashboard',
  templateUrl: './quotation-dashboard.component.html',
  styleUrls: ['./quotation-dashboard.component.scss'],
  providers: [MessageService],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideInDown', [
      transition(':enter', [
        style({ transform: 'translateY(-20px)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ]),
    trigger('slideInUp', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ]),
    trigger('slideInLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-20px)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(20px)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ]),
    trigger('staggerIn', [
      transition('* => *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          stagger('100ms', [
            animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ]),
    trigger('hoverScale', [
      state('normal', style({ transform: 'scale(1)' })),
      state('hover', style({ transform: 'scale(1.03)' })),
      transition('normal <=> hover', animate('200ms ease-in-out'))
    ])
  ]
})

export class QuotationDashboardComponent implements OnInit {
  isHeaderCollapsed = false;
  workflowItems: QuotationItem[] = [];
  filteredItems: QuotationItem[] = [];
  aggregatedItems: any[] = [];
  totalItems: number = 0;
  customers: any[] = [];
  Indcustomers: any[] = [];
  years: number[] = [];
  calendarYears: YearOption[] = [];
  financialYears: YearOption[] = [];
  yearTypes: { name: string, value: string }[] = [
    { name: 'Calendar Year', value: 'calendar' },
    { name: 'Financial Year', value: 'financial' }
  ];
  selectedCustomer: any = 'all';
  selectedIndCustomer: any = null;
  selectedYearType: string | null = null;
  selectedCalendarYear: YearOption | null = null;
  selectedFinancialYear: YearOption | null = null;
  selectedMonth: number | null = null;
  availableMonths: any[] = [];
  paymentTrendData: any;
  paymentTrendOptions: any;
  paymentChartView: string = 'amount';
  chartViewOptions = [
    { label: 'Amount', value: 'amount' },
    { label: 'Percentage', value: 'percentage' }
  ];
  selectedTrend: string = 'revenue';
  trendOptions = [
    { label: 'Revenue', value: 'revenue' },
    { label: 'Quotations', value: 'quotations' }
  ];
  selectedComparison: string = 'amount';
  comparisonOptions = [
    { label: 'Amount', value: 'amount' },
    { label: 'Count', value: 'count' }
  ];
  selectedCustomers: string[] = [];
  startYear: number | null = null;
  endYear: number | null = null;
  CustomerStartYear: number | null = null;
  CustomerEndYear: number | null = null;
  yearOptions: any[] = [];
  CustomeryearOptions: any[] = [];
  selectedDataType: string = 'poValue'; // Default to 'revenue'
  paymentStatusData: ChartData = { labels: [], datasets: [] };
  monthlyRevenueData: ChartData = { labels: [], datasets: [] };
  customerDistributionData: ChartData = { labels: [], datasets: [] };
  yearlyAmountsData: ChartData = { labels: [], datasets: [] };
  yearlyAmountsOptions: any;
  totalQuotations: number = 0;
  filteredQuotations: number = 0;
  totalQuarter: number = 0;
  filterQuarter: number = 0;
  paidQuotations: number = 0;
  unpaidQuotations: number = 0;
  totalRavenue: number = 0;
  invoiceAmount: number = 0;
  totalInvoice: number = 0;
  poAmount: number = 0;
  totalpo: number = 0;
  unpaidAmount: number = 0;
  topCustomer: TopCustomer | null = null;
  peakMonth: MonthData | null = null;
  lowestMonth: MonthData | null = null;
  yearlyGrowth: number = 0;
  averageQuotationValue: number = 0;
  quotationsThisMonth: number = 0;
  revenueThisMonth: number = 0;
  paymentChartOptions: any;
  lineChartOptions: any;
  pieChartOptions: any;
  barChartOptions: any;
  isLineChart: boolean = false;
  chartType: string = 'bar';
  chartOptions: any;
  loading: boolean = true;
  tableLoading: boolean = false;
  isCustomerLineChart: boolean = true; // Default to Line chart
  customerChartType: string = 'line'; // Default chart type
  customerChartOptions: any; // Options for the customer distribution chart
  selectedIndYearType: string = 'calendar';
  // Dialog properties
  displayDialog: boolean = false;
  dialogChartData: any;
  dialogChartLabels: string[] = [];
  dialogChartType: string = '';
  dialogChartOptions: any; // New property for dialog-specific chart options
  // Total Sales Chart properties
  isTotalSalesLineChart: boolean = false; // Default to Line chart
  totalSalesChartType: string = 'bar'; // Default chart type
  totalSalesChartOptions: any; // Chart options
  totalSalesData: any = { labels: [], datasets: [] }; // Chart data
  selectedTotalSalesCustomer: string = 'all'; // Default to "All" customers
  selectedTotalSalesYearType: string = 'calendar'; // Default to calendar year
  totalSalesStartYear: number | null = null; // Start year
  totalSalesEndYear: number | null = null; // End year
  // Annual Sales Target Chart properties
  isAccumulativeLineChart: boolean = true; // Default to Line chart
  annualSalesData: any = { labels: [], datasets: [] }; // Chart data
  annualSalesChartOptions: any; // Chart options
  selectedTotalAnnualSalesCustomer: string = 'all'; // Default to "All"
  selectedTotalAnnualSalesYearType: string = 'calendar'; // Default to calendar year
  selectedTotalAnnualSalesYear: number | null = null; // Selected year
  annualSalesChartType: string = 'line'; // Default chart type
  annualSalesTargets: Map<string, Map<number, number>> = new Map(); // Customer -> Year -> Target (optional)
  yearTypeSwitch: boolean = false;
  ref: DynamicDialogRef | undefined;

  private monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  background: any;
  filter: boolean = false;
  showPoVsInvChart: boolean = true; // Default to PO vs INV view
  poVsInvData: any;
  invVsRevData: any;
  poVsInvOptions: any;
  invVsRevOptions: any;
  kikF1Data: any[] = []; // Raw KIK#F1 data
  kikF1MonthlyTotals: Map<string, number> = new Map();
  chartViewIndex: number = 1; // Start with PO vs INV (original default)
  currentChartView: 'quote-vs-po' | 'po-vs-inv' | 'inv-vs-rev' = 'po-vs-inv';
  quoteVsPoData: any;
  quoteVsPoOptions: any;
  isToggleOn: boolean = false; // For toggle binding
  detailDialogVisible = false;
  detailDialogType: 'quotation' | 'po' | 'invoice' | 'revenue' = 'quotation';
  detailDialogItems: any[] = [];
  filteredDetailItems: any[] = [];
  selectedDetailItem: any = null;
  detailSearchTerm = '';
  detailDialogTotalAmount = 0;
  detailSummaryStrip: any[] = [];
  detailFooterTotals: any[] = [];
  openTreeCards = new Set<string>();
  readonly workflowSteps = [
    { key: 'quotation', label: 'Quote', icon: 'pi pi-file' },
    { key: 'po', label: 'PO', icon: 'pi pi-shopping-cart' },
    { key: 'pi', label: 'PI', icon: 'pi pi-file-edit' },
    { key: 'workOrder', label: 'Work Order', icon: 'pi pi-cog' },
    { key: 'ci', label: 'Invoice', icon: 'pi pi-file-invoice' },
    { key: 'payment', label: 'Payment', icon: 'pi pi-check-circle' },
  ];
  readonly dialogConfig: Record<string, { title: string; subtitle: string; icon: string }> = {
  quotation: { title: 'Quotation Details',  subtitle: 'All quotations in selected period', icon: 'pi pi-file' },
  po:        { title: 'Purchase Order Details', subtitle: 'POs in selected period',        icon: 'pi pi-shopping-cart' },
  invoice:   { title: 'Invoice Details',    subtitle: 'Invoices raised in selected period',icon: 'pi pi-file-invoice' },
  revenue:   { title: 'Revenue Details',    subtitle: 'Payments received in selected period', icon: 'pi pi-dollar' },
};

  readonly stepOrder = ['quotation', 'po', 'pi', 'workOrder', 'ci', 'payment'];
  constructor(
    private messageService: MessageService,
    private workflowDataService: WorkflowDataService,
    private cdr: ChangeDetectorRef, // Add this
    private dialogService: DialogService
  ) {
    this.initChartOptions();
  }

  ngOnInit(): void {
    // Set default year type to calendar
    this.selectedYearType = 'calendar';
    this.yearTypeSwitch = false;

    // Setup years
    this.setupCalendarYears();
    this.setupFinancialYears();

    // Set default current year
    this.setDefaultCurrentYear();

    // Initialize available months
    this.setupAvailableMonths();

    // Initialize other data...
    this.loadData();
  }
  onYearTypeToggle(): void {
    // Update selectedYearType based on toggle
    this.selectedYearType = this.yearTypeSwitch ? 'financial' : 'calendar';


    // Reset previous selections
    this.selectedCalendarYear = null;
    this.selectedFinancialYear = null;
    this.selectedMonth = null;

    // Ensure years are setup
    if (this.calendarYears.length === 0) {
      this.setupCalendarYears();
    }
    if (this.financialYears.length === 0) {
      this.setupFinancialYears();
    }

    // Set current year based on selected type
    this.setDefaultCurrentYear();

    // Setup months if not already done
    if (this.availableMonths.length === 0) {
      this.setupAvailableMonths();
    }

    // Optionally apply filters immediately after switching
    this.applyFilters();
  }
  getToggleOnLabel(): string {
    // The ON label shows what you'll switch TO (next chart)
    switch (this.chartViewIndex) {
      case 0: // Current: Quote vs PO (index 0) → Next: PO vs INV (index 1)
        return 'PO vs INV';
      case 1: // Current: PO vs INV (index 1) → Next: INV vs REV (index 2)
        return 'INV vs REV';
      case 2: // Current: INV vs REV (index 2) → Next: Quote vs PO (index 0)
        return 'Quote vs PO';
      default:
        return 'INV vs REV'; // Default for PO vs INV
    }
  }
  getToggleOffLabel(): string {
    // The OFF label also shows what you'll switch TO (next chart)
    // This creates a unified button that always shows the next destination
    switch (this.chartViewIndex) {
      case 0: // Current: Quote vs PO → Next: PO vs INV
        return 'PO vs INV';
      case 1: // Current: PO vs INV → Next: INV vs REV
        return 'INV vs REV';
      case 2: // Current: INV vs REV → Next: Quote vs PO
        return 'Quote vs PO';
      default:
        return 'INV vs REV';
    }
  }
  private setDefaultCurrentYear(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based (0 = January, 3 = April)

    if (this.selectedYearType === 'calendar') {
      // Find current calendar year
      const currentYearOption = this.calendarYears.find(year =>
        year.value.start.getFullYear() === currentYear
      );
      if (currentYearOption) {
        this.selectedCalendarYear = currentYearOption;
      }
    } else if (this.selectedYearType === 'financial') {
      // Determine current financial year
      let financialYearStart: number;

      // Financial year runs from April 1st to March 31st
      // If current month is April (3) or later, we're in FY starting this year
      // If before April, we're in FY that started last year
      if (currentMonth >= 3) { // April or later (April = month 3)
        financialYearStart = currentYear;
      } else { // January to March
        financialYearStart = currentYear - 1;
      }

      // Find the corresponding financial year option
      const currentFinancialYearOption = this.financialYears.find(year => {
        const yearStart = parseInt(year.name.split('-')[0]);
        return yearStart === financialYearStart;
      });

      if (currentFinancialYearOption) {
        this.selectedFinancialYear = currentFinancialYearOption;
      }
    }
  }
  private setupAvailableMonths(): void {
    this.availableMonths = [
      { name: 'January', value: 1 },
      { name: 'February', value: 2 },
      { name: 'March', value: 3 },
      { name: 'April', value: 4 },
      { name: 'May', value: 5 },
      { name: 'June', value: 6 },
      { name: 'July', value: 7 },
      { name: 'August', value: 8 },
      { name: 'September', value: 9 },
      { name: 'October', value: 10 },
      { name: 'November', value: 11 },
      { name: 'December', value: 12 }
    ];
  }
  onHeaderCollapse(isCollapsed: boolean) {
    this.isHeaderCollapsed = isCollapsed;
  }
  openChartDialog(chartType: string): void {
    this.dialogChartType = chartType;

    // Set dialogChartData, dialogChartLabels, and chartType based on chartType
    switch (chartType) {
      case 'monthlyRevenue':
        this.dialogChartData = this.monthlyRevenueData;
        this.dialogChartLabels = this.monthlyRevenueData.labels || [];
        this.chartType = this.isLineChart ? 'line' : 'bar';
        this.dialogChartOptions = JSON.parse(JSON.stringify(this.chartOptions)); // Use monthlyRevenue chartOptions
        break;
      case 'customerDistribution':
        this.dialogChartData = this.customerDistributionData;
        this.dialogChartLabels = this.customerDistributionData.labels || [];
        this.chartType = this.isCustomerLineChart ? 'line' : 'bar';
        this.dialogChartOptions = JSON.parse(JSON.stringify(this.customerChartOptions)); // Use customerChartOptions
        break;
      case 'totalSales':
        this.dialogChartData = this.totalSalesData;
        this.dialogChartLabels = this.totalSalesData.labels || [];
        this.chartType = this.isTotalSalesLineChart ? 'line' : 'bar';
        this.startYear = this.totalSalesStartYear;
        this.endYear = this.totalSalesEndYear;
        this.dialogChartOptions = JSON.parse(JSON.stringify(this.totalSalesChartOptions)); // Use totalSalesChartOptions
        break;
      case 'annualSales':
        this.dialogChartData = this.annualSalesData;
        this.dialogChartLabels = this.annualSalesData.labels || [];
        this.chartType = 'line'; // annualSales is always a line chart
        this.startYear = this.selectedTotalAnnualSalesYear;
        this.dialogChartOptions = JSON.parse(JSON.stringify(this.annualSalesChartOptions)); // Use annualSalesChartOptions
        break;
      default:
        this.dialogChartData = { labels: [], datasets: [] };
        this.dialogChartLabels = [];
        this.dialogChartOptions = {};
        this.chartType = 'bar';
        return;
    }

    // Ensure legend is enabled for dialog
    this.dialogChartOptions.plugins.legend = {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 }
      }
    };

    // Ensure datalabels plugin is configured consistently
    this.dialogChartOptions.plugins.datalabels = {
      anchor: 'end',
      align: chartType === 'annualSales' || this.chartType === 'line' ? 'top' : 'top', // Above points for line charts, above bars for bar charts
      formatter: (value: number, context: any) => {
        const roundedValue = Math.round(value); // Round to nearest integer
        if (roundedValue <= 0) return '';

        if (chartType === 'annualSales') {
          // For annualSales, use only K format and show only total (blue) dataset
          if (context.datasetIndex !== 1) return ''; // Only show for KIK#F1 (index 1)

          const roundedValue = Math.round(value); // Ensure consistent rounding
          const thousand = roundedValue / 1000;

          // Compact K format for cumulative totals (no M, only K or plain dollars)
          if (roundedValue >= 1000) {
            return `$${Math.round(thousand)}`;
          } else {
            // Under 1K as plain dollars
            return `$${roundedValue}`;
          }
        }
        // For other chart types, keep existing formatting
        if (roundedValue < 1000) {
          // For values less than 1000, show one decimal place
          return `$${(roundedValue / 1000).toFixed(1)}`;
        }

        // For values 1000 or greater, show no decimal places
        const thousand = (roundedValue / 1000).toFixed(0);
        return `$${thousand}`;
      },
      color: (context: any) => {
        // For annualSales, show only blue color for KIK#F1 dataset
        if (chartType === 'annualSales') {
          return context.datasetIndex === 1 ? '#1d1d1dff' : 'transparent';
        }
        // For other charts, use black
        return '#000';
      },
      font: {
        size: chartType === 'annualSales' ? 10 : 12,
        weight: 'bold'
      },
      // Remove background and border for clean text (especially for annualSales)
      backgroundColor: null,
      borderColor: null,
      borderWidth: 0,
      padding: chartType === 'annualSales' ? 0 : 4,
      textStrokeColor: 'transparent',
      textStrokeWidth: 0,
      borderRadius: 0,
      clamp: false,
      // Rotation only for non-annualSales bar charts
      rotation: chartType === 'annualSales' || this.chartType === 'line' ? 0 : -90
    };

    // Update y-axis title based on selectedDataType
    this.dialogChartOptions.scales.y.title.text =
      chartType === 'annualSales'
        ? 'Cumulative Amount (USD)'
        : this.selectedDataType === 'revenue'
          ? 'Revenue (USD)'
          : this.selectedDataType === 'invoiceValue'
            ? 'Invoice (USD)'
            : 'PO (USD)';

    // Ensure consistent y-axis tick formatting
    this.dialogChartOptions.scales.y.ticks.callback = (value: any) => {
      const roundedValue = Math.round(value); // Round to nearest integer

      if (chartType === 'annualSales') {
        // For annualSales, use only K format for y-axis ticks (no M)
        if (roundedValue >= 1000) {
          return `$${Math.round(roundedValue / 1000)}K`;
        }
        return `$${roundedValue}`;
      }

      // For all other charts, use only K format (no M)
      if (roundedValue >= 1000) {
        return `$${Math.round(roundedValue / 1000)}K`;
      }
      return `$${roundedValue}`;
    };


    this.displayDialog = true;
  }


  toggleChartType(): void {
    this.isLineChart = !this.isLineChart;
    this.chartType = this.isLineChart ? 'line' : 'bar';
    this.prepareMonthlyRevenueChart();
  }
  toggleCustomerChartType(): void {
    this.isCustomerLineChart = !this.isCustomerLineChart;
    this.customerChartType = this.isCustomerLineChart ? 'line' : 'bar';
    this.prepareCustomerDistributionChart();
  }
  initChartOptions(): void {
    this.paymentChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.raw || 0;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = Math.round((value / total) * 100);
              return `${label}: ${this.formatCurrency(value)} (${percentage}%)`;
            }
          }
        }
      }
    };
    this.totalSalesChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false // Hide legend in main dashboard charts
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${this.formatCurrency(context.raw)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grace: '10%',
          title: {
            display: true,
            text: 'Revenue (USD)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => this.formatCurrency(value),
            font: { size: 11 }
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
      }
    };
    this.annualSalesChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${context.raw.toFixed(2)}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Progress (%)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => `${value}%`,
            font: { size: 11 }
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
      }
    };
    this.lineChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false // Hide legend in main dashboard charts
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${this.formatCurrency(context.raw)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grace: '10%',
          title: {
            display: true,
            text: 'Revenue (USD)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => this.formatCurrency(value),
            font: { size: 11 }
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
      }
    };
    this.customerChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false // Hide legend in main dashboard charts
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${this.formatCurrency(context.raw)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grace: '10%',
          title: {
            display: true,
            text: 'Revenue (USD)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => this.formatCurrency(value),
            font: { size: 11 }
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
      }
    };
    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${context.raw} quotations`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => value // Show plain numbers
          },
          title: {
            display: true,
            text: 'Number of Quotations',
            font: { weight: 'bold' }
          }
        },
        x: {
          title: {
            display: true,
            text: 'Year',
            font: { weight: 'bold' }
          }
        }
      }
    };
    this.chartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${this.formatCurrency(context.raw)}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grace: '10%',
          title: {
            display: true,
            text: 'Revenue (USD)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => this.formatCurrency(value),
            font: { size: 11 }
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
      }
    };
  }

  loadData(): void {
    this.loading = true;
    this.workflowDataService.workflowItems$.subscribe({
      next: (items) => {
        const filteredSourceItems = items.filter(item =>
          !(item.data.jobNumber &&
            item.data.jobNumber.toString().toUpperCase().startsWith('DRW'))
        );
        this.workflowItems = filteredSourceItems.map(item => ({
          ...item.data,
          orderId: item.orderId,
          currentStep: item.currentStep,
          completed: item.completed,
          uuid: item.uuid,
          paymentPrice: Number(item.data.paymentPrice) || 0,
          invoicePrice: Number(item.data.invoicePrice) || 0,
          totalPrice: Number(item.data.totalPrice) || 0
        }));
        this.filteredItems = [...this.workflowItems];
        this.setupFilterOptions();
        this.setupYearOptions();
        this.calculateMetrics();
        this.loadKikF1Data();

        // Use setTimeout to defer the assignment to the next tick
        setTimeout(() => {
          this.selectedIndCustomer = this.getTopCustomerByDataType(this.selectedDataType);
          if (!this.customers.some(c => c.value === 'all')) {
            this.customers.unshift({ label: 'All', value: 'all' });
          }
          this.selectedTotalAnnualSalesYear = new Date().getFullYear();
          this.selectedTotalSalesCustomer = 'all'
          this.selectedTotalAnnualSalesCustomer = 'all'
          this.updateMonthlyRevenueChart();
          this.updateAnnualSalesChart();
          this.updateTotalSalesChart()
          this.calculateRevenueThisMonth();
          this.applyFilters();
          this.cdr.detectChanges(); // Manually trigger change detection
        }, 0);

        this.prepareChartData();
        this.applyChartFilters();
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load workflow data from backend',
          life: 5000
        });
        this.loading = false;
      }
    });
  }
  // Create a safer method to get display name
  getSelectedCustomerDisplayName(): string {
    if (!this.selectedIndCustomer) {
      return 'None Selected';
    }
    if (this.selectedIndCustomer === 'all') {
      return 'All Customers';
    }
    // Verify the customer exists in the customers array
    const customerExists = this.Indcustomers.some(c => c.value === this.selectedIndCustomer);
    return customerExists ? this.selectedIndCustomer : 'None Selected';
  }
  // Utility function to normalize customer names (first two words, case-insensitive)
  private normalizeCustomerName(name: string): string {
    if (!name) return '';
    // Trim, normalize spaces, and convert to lowercase
    const cleanedName = name.trim().toLowerCase().replace(/\s+/g, ' ');
    // Split into words and take first two words (or fewer if not available)
    const words = cleanedName.split(' ');
    return words.slice(0, 2).join(' ');
  }

  // applyFilters method with normalized customer name comparison
  applyFilters(): void {
    this.tableLoading = true;
    this.filter = true;

    // Keep the original filteredItems for PO-related calculations
    this.filteredItems = this.workflowItems.filter(item => {
      let match = true;

      // Customer filter
      if (this.selectedCustomer) {
        const normalizedSelected = this.normalizeCustomerName(this.selectedCustomer).toLowerCase();
        if (normalizedSelected === 'all') {
        } else {
          const customerMatch = this.normalizeCustomerName(item.customerName) === this.normalizeCustomerName(this.selectedCustomer);
          match = match && customerMatch;
        }
      }

      // Date validation for poDate (for PO-related metrics)
      const itemDate = new Date(item.quotationDate);
      if (isNaN(itemDate.getTime())) {
        return false;
      }

      // Year type filter
      if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
        const yearMatch = itemDate >= this.selectedCalendarYear.value.start && itemDate <= this.selectedCalendarYear.value.end;
        match = match && yearMatch;
      } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
        const yearMatch = itemDate >= this.selectedFinancialYear.value.start && itemDate <= this.selectedFinancialYear.value.end;
        match = match && yearMatch;
      }

      // Month filter
      if (this.selectedMonth) {
        const monthMatch = itemDate.getMonth() + 1 === Number(this.selectedMonth);
        match = match && monthMatch;
      }

      return match;
    });

    this.calculateMetrics();
    this.prepareChartData();
    this.tableLoading = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Filters Applied',
      detail: 'Dashboard data has been filtered',
      life: 3000
    });

    // Sync parent selections to child charts only if a specific customer is selected
    if (this.selectedCustomer) {
      this.selectedIndCustomer = this.selectedCustomer;
      this.selectedTotalSalesCustomer = this.selectedCustomer;
      this.selectedTotalAnnualSalesCustomer = this.selectedCustomer;
    }

    // Year type sync with null check to handle TypeScript error
    const yearType = this.selectedYearType || 'calendar'; // Default to 'calendar' if null
    this.selectedIndYearType = yearType;
    this.selectedTotalSalesYearType = yearType;
    this.selectedTotalAnnualSalesYearType = yearType;

    // Year sync (parent selects single year, so set child start/end to same year; extract year from name)
    let selectedYearNumber: number | null = null;
    if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
      selectedYearNumber = parseInt(this.selectedCalendarYear.name, 10);
    } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
      // Assuming financial year name is like '2024' or '2024-2025'; extract starting year
      const financialNameParts = this.selectedFinancialYear.name.split('-');
      selectedYearNumber = parseInt(financialNameParts[0], 10);
    }

    // Trigger child chart updates after sync
    this.updateMonthlyRevenueChart();
    this.updateTotalSalesChart();
    this.updateAnnualSalesChart();
  }


  setupFilterOptions(): void {
    // Create a map to store original names for display
    const nameMap = new Map<string, string>(); // normalized -> original
    this.workflowItems.forEach(item => {
      if (item.customerName) {
        const normalized = this.normalizeCustomerName(item.customerName);
        // Store the first occurrence of the original name for display
        if (!nameMap.has(normalized)) {
          nameMap.set(normalized, item.customerName);
        }
      }
    });

    // Create unique customer list using normalized names
    this.customers = Array.from(nameMap.entries()).map(([normalized, original]) => ({
      label: original, // Use original name for display
      value: original // Use original name for selection
    }));
    this.Indcustomers = Array.from(nameMap.entries()).map(([normalized, original]) => ({
      label: original, // Use original name for display
      value: original // Use original name for selection
    }));
    this.setupCalendarYears();
    this.setupFinancialYears();

    // Calculate item counts based on selectedDataType and year range
    const customerItemCounts = new Map<string, number>();
    this.workflowItems.forEach(item => {
      if (item.customerName && item.quotationDate) {
        const date = new Date(item.quotationDate);
        const year = date.getFullYear();
        // Only consider items within the selected year range
        if (this.startYear && this.endYear && year >= this.startYear && year <= this.endYear) {
          const normalized = this.normalizeCustomerName(item.customerName);
          let isValidItem = false;

          // Calculate totals from arrays
          const totalPayments = (item.payments || []).reduce((sum: number, pay: any) => sum + (pay.paymentPrice || 0), 0);
          const totalInvoices = (item.invoices || []).reduce((sum: number, inv: any) => sum + (inv.invoicePrice || 0), 0);

          if (this.selectedDataType === 'revenue' && totalPayments > 0) {
            isValidItem = true;
          } else if (this.selectedDataType === 'invoiceValue' && totalInvoices > 0) {
            isValidItem = true;
          } else if (this.selectedDataType === 'poValue' && item.poNumber && item.totalPrice) {
            isValidItem = true;
          }

          if (isValidItem) {
            const currentCount = customerItemCounts.get(normalized) || 0;
            customerItemCounts.set(normalized, currentCount + 1);
          }
        }
      }
    });


    // Select top 2 customers based on item count within the year range
    this.selectedCustomers = Array.from(customerItemCounts.entries())
      .filter(([_, count]) => count > 0) // Exclude customers with zero items
      .sort((a, b) => b[1] - a[1]) // Sort by item count in descending order
      .slice(0, 2) // Take top 2
      .map(([normalized]) => nameMap.get(normalized)!); // Map back to original name

    // Update the chart to reflect the new selection
    this.updateCustomerChart();
  }

  getMetricTitle(baseTitle: string): string {
    let title = baseTitle;

    // Add month if selected
    if (this.selectedMonth) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthNames[Number(this.selectedMonth) - 1];
      title += ` ${monthName}`;
    }

    // Add year based on year type
    if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
      const year = this.selectedCalendarYear.value.start.getFullYear();
      title += ` ${year}`;
    } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
      const startYear = this.selectedFinancialYear.value.start.getFullYear();
      const endYear = this.selectedFinancialYear.value.end.getFullYear();
      title += ` ${startYear}-${endYear}`;
    }

    return title;
  }

  applyChartFilters(): void {
    if (this.startYear && this.endYear && this.startYear > this.endYear) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Start year cannot be greater than end year',
        life: 3000
      });
      this.endYear = this.startYear;
    }
    this.setupFilterOptions(); // Call setupFilterOptions to handle customer selection and chart update
    this.updateCustomerChart();
  }
  setupYearOptions(): void {
    const currentYear = new Date().getFullYear();
    this.yearOptions = [];
    for (let i = currentYear - 10; i <= currentYear; i++) {
      this.yearOptions.push({ label: i.toString(), value: i });
    }
    this.CustomeryearOptions = [];
    for (let i = currentYear - 10; i <= currentYear; i++) {
      this.CustomeryearOptions.push({ label: i.toString(), value: i });
    }
    this.startYear = currentYear - 5;
    this.endYear = currentYear;
    this.CustomerStartYear = currentYear - 5;
    this.CustomerEndYear = currentYear;
    this.totalSalesStartYear = currentYear - 5;
    this.totalSalesEndYear = currentYear
  }

  setupCalendarYears(): void {
    this.calendarYears = []; // Clear existing
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      const startDate = new Date(i, 0, 1);
      const endDate = new Date(i, 11, 31);
      this.calendarYears.push({
        name: i.toString(),
        value: { start: startDate, end: endDate }
      });
    }
    this.calendarYears.sort((a, b) => parseInt(b.name) - parseInt(a.name));
  }

  setupFinancialYears(): void {
    this.financialYears = []; // Clear existing
    const currentYear = new Date().getFullYear();

    // Create financial years from 5 years ago to next year
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      const startDate = new Date(i, 3, 1); // April 1st
      const endDate = new Date(i + 1, 2, 31); // March 31st next year
      this.financialYears.push({
        name: `${i}-${i + 1}`,
        value: { start: startDate, end: endDate }
      });
    }

    // Sort in descending order (most recent first)
    this.financialYears.sort((a, b) => {
      const yearA = parseInt(a.name.split('-')[0]);
      const yearB = parseInt(b.name.split('-')[0]);
      return yearB - yearA;
    });

  }

  resetFilters(): void {
    this.selectedCalendarYear = null;
    this.selectedFinancialYear = null;
    this.selectedMonth = null;
    this.filteredItems = [...this.workflowItems];
    this.calculateMetrics();
    this.prepareChartData();
    this.messageService.add({
      severity: 'info',
      summary: 'Filters Reset',
      detail: 'All filters have been cleared',
      life: 3000
    });
  }

  onYearTypeChange(): void {
    // Reset selections when year type changes
    this.selectedCalendarYear = null;
    this.selectedFinancialYear = null;
    this.selectedMonth = null;

    // Setup months if not already done
    if (this.availableMonths.length === 0) {
      this.setupAvailableMonths();
    }
  }


  calculateMetrics(): void {
    this.totalQuotations = this.workflowItems.length;

    // Calculate quotation metrics using quotation-filtered items
    this.filteredQuotations = [...new Set(this.filteredItems.map(item => item.quotationNo))].length;
    this.totalQuarter = this.filteredItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    this.filterQuarter = this.filteredItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    this.averageQuotationValue = this.filteredQuotations > 0 ? this.filterQuarter / this.filteredQuotations : 0;

    // Custom filter for paidItems: Apply similar filters, but use paymentReceivedDate from payments array
    const paidItems = this.workflowItems.filter(item => {
      let match = true;
      if (this.selectedCustomer) {
        const normalizedSelected = this.normalizeCustomerName(this.selectedCustomer).toLowerCase();
        if (normalizedSelected !== 'all') {
          match = match && (this.normalizeCustomerName(item.customerName) === this.normalizeCustomerName(this.selectedCustomer));
        }
      }

      const hasMatchingPayment = (item.payments || []).some((pay: any) => {
        const paymentDate = new Date(pay.paymentReceivedDate);
        if (isNaN(paymentDate.getTime())) return false;

        let dateMatch = true;

        if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
          dateMatch = dateMatch && (paymentDate >= this.selectedCalendarYear.value.start && paymentDate <= this.selectedCalendarYear.value.end);
        } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
          dateMatch = dateMatch && (paymentDate >= this.selectedFinancialYear.value.start && paymentDate <= this.selectedFinancialYear.value.end);
        }

        if (this.selectedMonth) {
          dateMatch = dateMatch && (paymentDate.getMonth() + 1 === Number(this.selectedMonth));
        }

        return dateMatch;
      });

      return match && hasMatchingPayment;
    });

    // Calculate totalRevenue by filtering payments within each item based on date criteria
    this.totalRavenue = parseFloat(paidItems.reduce((sum: number, item: any) => {
      const filteredPayments = (item.payments || []).filter((pay: any) => {
        const paymentDate = new Date(pay.paymentReceivedDate);
        if (isNaN(paymentDate.getTime())) return false;

        let dateMatch = true;

        if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
          dateMatch = dateMatch && (paymentDate >= this.selectedCalendarYear.value.start && paymentDate <= this.selectedCalendarYear.value.end);
        } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
          dateMatch = dateMatch && (paymentDate >= this.selectedFinancialYear.value.start && paymentDate <= this.selectedFinancialYear.value.end);
        }

        if (this.selectedMonth) {
          dateMatch = dateMatch && (paymentDate.getMonth() + 1 === Number(this.selectedMonth));
        }

        return dateMatch;
      });

      const itemSum = filteredPayments.reduce((paySum: number, pay: any) => paySum + (pay.paymentPrice || 0), 0);
      return sum + itemSum;
    }, 0).toFixed(2));

    // Filter to get qualifying items based on invoice date
    const totalInvoice = this.workflowItems.filter(item => {
      let match = true;

      if (!item.invoices || item.invoices.length === 0) return false;

      if (this.selectedCustomer) {
        const normalizedSelected = this.normalizeCustomerName(this.selectedCustomer).toLowerCase();
        if (normalizedSelected !== 'all') {
          const customerMatch = this.normalizeCustomerName(item.customerName) === this.normalizeCustomerName(this.selectedCustomer);
          match = match && customerMatch;
        }
      }

      const hasMatchingInvoice = item.invoices.some((inv) => {
        const invoiceDate = new Date(inv.date);
        if (isNaN(invoiceDate.getTime())) return false;

        let yearMatch = true;
        if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
          yearMatch = invoiceDate >= this.selectedCalendarYear.value.start && invoiceDate <= this.selectedCalendarYear.value.end;
        } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
          yearMatch = invoiceDate >= this.selectedFinancialYear.value.start && invoiceDate <= this.selectedFinancialYear.value.end;
        }

        const monthMatch = this.selectedMonth ? invoiceDate.getMonth() + 1 === Number(this.selectedMonth) : true;

        return yearMatch && monthMatch;
      });

      return match && hasMatchingInvoice;
    });

    this.totalInvoice = totalInvoice.length;
    console.log('totalInvoice', totalInvoice)
    // Calculate invoiceAmount (remove the duplicate calculation)
    this.invoiceAmount = totalInvoice.reduce((total, item) => {
      const itemTotal = item.invoices.reduce((invoiceSum: number, invoice: any) => {
        const invoiceDate = new Date(invoice.date);
        if (isNaN(invoiceDate.getTime())) return invoiceSum;

        let yearMatch = true;
        if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
          yearMatch = invoiceDate >= this.selectedCalendarYear.value.start && invoiceDate <= this.selectedCalendarYear.value.end;
        } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
          yearMatch = invoiceDate >= this.selectedFinancialYear.value.start && invoiceDate <= this.selectedFinancialYear.value.end;
        }

        const monthMatch = this.selectedMonth ? invoiceDate.getMonth() + 1 === Number(this.selectedMonth) : true;

        if (yearMatch && monthMatch) {
          return invoiceSum + (invoice.invoicePrice || 0);
        }
        return invoiceSum;
      }, 0);
      return total + itemTotal;
    }, 0);

    // PO calculations use filteredItems (already filtered by poDate in applyFilters)
    const totalPo = this.workflowItems.filter(item => {
      // Check if item has poNumber AND a valid poDate (not empty string)
      if (!item.poNumber || !item.poDate || item.poDate.trim() === '') return false;

      let match = true;

      // Apply customer filter if selected
      if (this.selectedCustomer) {
        const normalizedSelected = this.normalizeCustomerName(this.selectedCustomer).toLowerCase();
        if (normalizedSelected !== 'all') {
          match = match && (this.normalizeCustomerName(item.customerName) === this.normalizeCustomerName(this.selectedCustomer));
        }
      }

      // Apply date filters based on poDate
      const poDate = new Date(item.poDate);
      if (!isNaN(poDate.getTime())) {
        let dateMatch = true;

        // Year filter
        if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
          dateMatch = dateMatch && (poDate >= this.selectedCalendarYear.value.start && poDate <= this.selectedCalendarYear.value.end);
        } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
          dateMatch = dateMatch && (poDate >= this.selectedFinancialYear.value.start && poDate <= this.selectedFinancialYear.value.end);
        }

        // Month filter
        if (this.selectedMonth) {
          dateMatch = dateMatch && (poDate.getMonth() + 1 === Number(this.selectedMonth));
        }

        match = match && dateMatch;
      } else {
        // If poDate is not a valid date, exclude it
        return false;
      }

      return match;
    });

    console.log('totalPo', totalPo)
    this.paidQuotations = paidItems.length;
    this.totalpo = totalPo.length;
    this.poAmount = totalPo.reduce((sum: number, item: any) => sum + (item.totalPrice || 0), 0);

    // Top customer calculation
    const customerRevenue = new Map<string, number>();
    this.filteredItems.forEach(item => {
      if (item.customerName) {
        const current = customerRevenue.get(item.customerName) || 0;
        const itemRevenue = item.totalPrice || 0;
        customerRevenue.set(item.customerName, current + itemRevenue);
      }
    });

    let maxRevenue = 0;
    customerRevenue.forEach((revenue, customer) => {
      if (revenue > maxRevenue) {
        maxRevenue = revenue;
        this.topCustomer = { name: customer, value: revenue };
      }
    });

    // Quotations this month
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    this.quotationsThisMonth = this.filteredItems.filter(item => {
      const date = new Date(item.quotationDate);
      return date.getMonth() + 1 === currentMonth && date.getFullYear() === currentYear;
    }).length;
  }


  prepareChartData(): void {
    this.preparePaymentCharts();
    this.prepareYearlyComparisonChart();
  }
  preparePaymentCharts(): void {
    this.prepareQuoteVsPoChart();  // New chart preparation
    this.preparePoVsInvChart();
    this.prepareInvVsRevChart();
    // Update current view based on toggle state
    this.onChartToggleChange();
  }
  switchToQuoteVsPoChart(): void {
    this.currentChartView = 'quote-vs-po';
  }
  onChartToggleChange(): void {
    // Cycle through the three views based on your specified order
    // The cycle: Quote vs PO (0) → PO vs INV (1) → INV vs REV (2) → Quote vs PO (0)
    this.chartViewIndex = (this.chartViewIndex + 1) % 3;

    // Update current chart view based on new index
    switch (this.chartViewIndex) {
      case 0:
        this.currentChartView = 'quote-vs-po';
        break;
      case 1:
        this.currentChartView = 'po-vs-inv';
        break;
      case 2:
        this.currentChartView = 'inv-vs-rev';
        break;
      default:
        this.currentChartView = 'po-vs-inv';
        this.chartViewIndex = 1;
    }

    // Update toggle state for visual consistency
    this.isToggleOn = !this.isToggleOn;
  }

  updateCurrentChartView(): void {
    // Initialize or sync chart view with index
    switch (this.chartViewIndex) {
      case 0:
        this.currentChartView = 'quote-vs-po';
        this.isToggleOn = false;
        break;
      case 1:
        this.currentChartView = 'po-vs-inv';
        this.isToggleOn = false;
        break;
      case 2:
        this.currentChartView = 'inv-vs-rev';
        this.isToggleOn = true;
        break;
      default:
        // Default to original PO vs INV (index 1)
        this.currentChartView = 'po-vs-inv';
        this.chartViewIndex = 1;
        this.isToggleOn = false;
    }
  }

  prepareQuoteVsPoChart(): void {
    // Calculate totals for Quotation vs PO based on dates
    const totalQuotePo = this.totalQuarter + this.poAmount;
    const quotePercentage = totalQuotePo > 0 ? (this.totalQuarter / totalQuotePo) * 100 : 0;
    const poPercentage = totalQuotePo > 0 ? (this.poAmount / totalQuotePo) * 100 : 0;

    this.quoteVsPoData = {
      labels: ['Quotations', 'Purchase Orders'],
      datasets: [{
        data: [quotePercentage, poPercentage],
        backgroundColor: ['#9C27B0', '#FF9800'], // Purple for Quote, Orange for PO
        hoverBackgroundColor: ['#7B1FA2', '#EF6C00'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 20
      }]
    };

    this.quoteVsPoOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        animateScale: true,
        animateRotate: true,
        duration: 1000,
        easing: 'easeOutQuart'
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 10,
              family: "'Inter', sans-serif"
            },
            color: '#1F2937'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(236, 48, 96, 0.9)',
          titleFont: { size: 10, family: "'Inter', sans-serif" },
          bodyFont: { size: 10, family: "'Inter', sans-serif" },
          padding: 7,
          cornerRadius: 8,
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.raw || 0;
              return `${label}: ${Math.round(value)}%`;
            }
          }
        },
        datalabels: {
          anchor: 'center',
          align: 'center',
          formatter: (value: number) => {
            return value > 0 ? `${Math.round(value)}%` : '';
          },
          color: '#302f2fff',
          font: { size: 12, weight: 'bold', family: "'Inter', sans-serif" },
          padding: 4
        }
      },
      elements: {
        arc: { borderRadius: 10 }
      }
    };
  }
  preparePoVsInvChart(): void {
    // Calculate totals for PO vs Invoice
    const totalPoInv = this.poAmount + this.invoiceAmount;
    const poPercentage = totalPoInv > 0 ? (this.poAmount / totalPoInv) * 100 : 0;
    const invPercentage = totalPoInv > 0 ? (this.invoiceAmount / totalPoInv) * 100 : 0;

    this.poVsInvData = {
      labels: ['Purchase Orders', 'Invoices'],
      datasets: [{
        data: [poPercentage, invPercentage],
        backgroundColor: ['#FF9800', '#2196F3'], // Orange for PO, Blue for INV
        hoverBackgroundColor: ['#EF6C00', '#1976D2'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 20
      }]
    };

    this.poVsInvOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        animateScale: true,
        animateRotate: true,
        duration: 1000,
        easing: 'easeOutQuart'
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 10,
              family: "'Inter', sans-serif"
            },
            color: '#1F2937'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(236, 48, 96, 0.9)',
          titleFont: { size: 10, family: "'Inter', sans-serif" },
          bodyFont: { size: 10, family: "'Inter', sans-serif" },
          padding: 7,
          cornerRadius: 8,
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.raw || 0;
              return `${label}: ${Math.round(value)}%`;
            }
          }
        },
        datalabels: {
          anchor: 'center',
          align: 'center',
          formatter: (value: number) => {
            return value > 0 ? `${Math.round(value)}%` : '';
          },
          color: '#302f2fff',
          font: { size: 12, weight: 'bold', family: "'Inter', sans-serif" },
          padding: 4
        }
      },
      elements: {
        arc: { borderRadius: 10 }
      }
    };
  }

  prepareInvVsRevChart(): void {
    // Calculate totals for Invoice vs Revenue
    const totalInvRev = this.invoiceAmount + this.totalRavenue;
    const invPercentage = totalInvRev > 0 ? (this.invoiceAmount / totalInvRev) * 100 : 0;
    const revPercentage = totalInvRev > 0 ? (this.totalRavenue / totalInvRev) * 100 : 0;

    this.invVsRevData = {
      labels: ['Invoices', 'Revenue'],
      datasets: [{
        data: [invPercentage, revPercentage],
        backgroundColor: ['#2196F3', '#4CAF50'], // Blue for INV, Green for Revenue
        hoverBackgroundColor: ['#1976D2', '#388E3C'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 20
      }]
    };

    this.invVsRevOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        animateScale: true,
        animateRotate: true,
        duration: 1000,
        easing: 'easeOutQuart'
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 10,
              family: "'Inter', sans-serif"
            },
            color: '#1F2937'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(236, 48, 96, 0.9)',
          titleFont: { size: 10, family: "'Inter', sans-serif" },
          bodyFont: { size: 10, family: "'Inter', sans-serif" },
          padding: 7,
          cornerRadius: 8,
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.raw || 0;
              return `${label}: ${Math.round(value)}%`;
            }
          }
        },
        datalabels: {
          anchor: 'center',
          align: 'center',
          formatter: (value: number) => {
            return value > 0 ? `${Math.round(value)}%` : '';
          },
          color: '#302f2fff',
          font: { size: 12, weight: 'bold', family: "'Inter', sans-serif" },
          padding: 4
        }
      },
      elements: {
        arc: { borderRadius: 10 }
      }
    };
  }
  getTopCustomerByDataType(dataType: string): string {
    if (!this.workflowItems || this.workflowItems.length === 0) {
      return 'all';
    }

    const customerStats = new Map<string, { count: number, total: number }>();

    this.workflowItems.forEach(item => {
      const customer = item.customerName || 'Unknown';

      // Calculate totals from arrays
      const totalPayments = (item.payments || []).reduce((sum: number, pay: any) => sum + (pay.paymentPrice || 0), 0);
      const totalInvoices = (item.invoices || []).reduce((sum: number, inv: any) => sum + (inv.invoicePrice || 0), 0);
      const poTotal = item.totalPrice || 0;  // Assuming PO value is totalPrice for now

      if (!customerStats.has(customer)) {
        customerStats.set(customer, { count: 0, total: 0 });
      }

      const stats = customerStats.get(customer)!;

      if ((dataType === 'revenue' && totalPayments > 0) ||
        (dataType === 'invoiceValue' && totalInvoices > 0) ||
        (dataType === 'poValue' && item.poNumber && poTotal > 0)) {
        stats.count++;
        stats.total += (dataType === 'revenue' ? totalPayments :
          dataType === 'invoiceValue' ? totalInvoices : poTotal);
      }
    });

    let topCustomer = 'all';
    let maxValue = 0;

    customerStats.forEach((stats, customer) => {
      const value = stats.total;
      if (value > maxValue) {
        maxValue = value;
        topCustomer = customer;
      }
    });

    const customerExists = this.Indcustomers.some(c => c.value === topCustomer);

    if (!customerExists && topCustomer !== 'all') {
      this.Indcustomers.push({ label: topCustomer, value: topCustomer }); // Add missing customer
      return topCustomer;
    }

    return customerExists ? topCustomer : 'all';
  }

  // Alternative approach - initialize with proper values
  ngAfterViewInit(): void {
    // This runs after the view is initialized, safer for setting initial values
    setTimeout(() => {
      if (!this.selectedIndCustomer && this.Indcustomers.length > 0) {
        this.selectedIndCustomer = this.getTopCustomerByDataType(this.selectedDataType);
        this.cdr.detectChanges();
      }
    }, 100);
  }
  prepareMonthlyRevenueChart(): void {
    // Initialize data structures
    const revenueByYear = new Map<number, Map<string, number>>(); // Year -> Month -> Value
    let maxValue = 0;
    let minValue = Infinity;
    this.peakMonth = null;
    this.lowestMonth = null;

    // Define months based on selectedIndYearType
    const months = this.selectedIndYearType === 'financial'
      ? ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Update yearOptions for FY display
    if (this.selectedIndYearType === 'financial') {
      this.CustomeryearOptions = Array.from({ length: 10 }, (_, i) => {
        const fyYear = 2020 + i;
        const startLabel = `Apr ${fyYear}`;
        const endLabel = `Mar ${fyYear + 1}`;
        return {
          label: `(${startLabel} - ${endLabel})`,
          value: fyYear
        };
      });
    } else {
      this.CustomeryearOptions = Array.from({ length: 10 }, (_, i) => ({
        label: `${2020 + i}`,
        value: 2020 + i
      }));
    }

    // **AGGREGATE DATA DIRECTLY** - No pre-filtering, filter during aggregation
    this.workflowItems.forEach((item: any) => {
      // Customer filter
      const customerMatch = this.selectedIndCustomer === 'all' ||
        this.normalizeCustomerName(item.customerName) === this.normalizeCustomerName(this.selectedIndCustomer);

      if (!customerMatch) return;

      if (this.selectedDataType === 'revenue') {
        (item.payments || []).forEach((pay: any) => {
          const itemDateStr = pay.paymentReceivedDate;
          if (!itemDateStr) return;

          const date = new Date(itemDateStr);
          if (isNaN(date.getTime())) return;

          let year: number, monthIndex: number, displayYear: number;

          if (this.selectedIndYearType === 'financial') {
            // **CORRECTED FY LOGIC**: April of this year to March of next year = FY [this year]
            const calendarYear = date.getFullYear();
            const month = date.getMonth();

            // Determine if this date belongs to the selected FY range
            if (month >= 3) { // April-Dec: belongs to FY [calendarYear]
              year = calendarYear;
              if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return; // Skip if outside FY range
            } else { // Jan-Mar: belongs to FY [calendarYear - 1]
              year = calendarYear - 1;
              if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return; // Skip if outside FY range
            }

            monthIndex = (month + 9) % 12; // April = 0, May = 1, ..., March = 11
            displayYear = year; // Always display the FY year

            console.log('revenue - date:', itemDateStr, 'month:', month, 'calendarYear:', calendarYear, 'fyYear:', year, 'displayYear:', displayYear, 'monthIndex:', monthIndex);
          } else {
            // Calendar year logic
            year = date.getFullYear();
            if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
            monthIndex = date.getMonth();
            displayYear = year;
          }

          const month = months[monthIndex];
          const monthYear = `${month} ${displayYear}`;

          // Initialize year map if not exists
          if (!revenueByYear.has(year)) {
            revenueByYear.set(year, new Map<string, number>());
            months.forEach(m => revenueByYear.get(year)!.set(m, 0));
          }

          const yearRevenue = revenueByYear.get(year)!;
          const currentValue = yearRevenue.get(month) || 0;
          const newValue = currentValue + (pay.paymentPrice || 0);
          yearRevenue.set(month, newValue);

          // Update peak and lowest months
          if (newValue > maxValue) {
            maxValue = newValue;
            this.peakMonth = { month: monthYear, value: newValue };
          }
          if (newValue < minValue && newValue > 0) {
            minValue = newValue;
            this.lowestMonth = { month: monthYear, value: newValue };
          } else if (minValue === Infinity && newValue === 0) {
            minValue = 0;
            this.lowestMonth = { month: monthYear, value: newValue };
          }
        });
      } else if (this.selectedDataType === 'invoiceValue') {
        (item.invoices || []).forEach((inv: any) => {
          const itemDateStr = inv.date;
          if (!itemDateStr) return;

          const date = new Date(itemDateStr);
          if (isNaN(date.getTime())) return;

          let year: number, monthIndex: number, displayYear: number;

          if (this.selectedIndYearType === 'financial') {
            const calendarYear = date.getFullYear();
            const month = date.getMonth();

            // **CORRECTED FY FILTERING**: Check if date belongs to selected FY range
            if (month >= 3) { // April-Dec: belongs to FY [calendarYear]
              year = calendarYear;
              if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
            } else { // Jan-Mar: belongs to FY [calendarYear - 1]
              year = calendarYear - 1;
              if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
            }

            monthIndex = (month + 9) % 12;
            displayYear = year;

            console.log('invoice - date:', itemDateStr, 'month:', month, 'calendarYear:', calendarYear, 'fyYear:', year, 'displayYear:', displayYear, 'monthIndex:', monthIndex);
          } else {
            // Calendar year logic
            year = date.getFullYear();
            if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
            monthIndex = date.getMonth();
            displayYear = year;
          }

          const month = months[monthIndex];
          const monthYear = `${month} ${displayYear}`;

          if (!revenueByYear.has(year)) {
            revenueByYear.set(year, new Map<string, number>());
            months.forEach(m => revenueByYear.get(year)!.set(m, 0));
          }

          const yearRevenue = revenueByYear.get(year)!;
          const currentValue = yearRevenue.get(month) || 0;
          const newValue = currentValue + (inv.invoicePrice || 0);
          yearRevenue.set(month, newValue);

          if (newValue > maxValue) {
            maxValue = newValue;
            this.peakMonth = { month: monthYear, value: newValue };
          }
          if (newValue < minValue && newValue > 0) {
            minValue = newValue;
            this.lowestMonth = { month: monthYear, value: newValue };
          } else if (minValue === Infinity && newValue === 0) {
            minValue = 0;
            this.lowestMonth = { month: monthYear, value: newValue };
          }
        });
      } else if (this.selectedDataType === 'poValue') {
        const itemDateStr = item.poDate;
        if (!itemDateStr) return;

        const date = new Date(itemDateStr);
        if (isNaN(date.getTime())) return;

        let year: number, monthIndex: number, displayYear: number;

        if (this.selectedIndYearType === 'financial') {
          const calendarYear = date.getFullYear();
          const month = date.getMonth();

          // **CORRECTED FY FILTERING** for PO
          if (month >= 3) { // April-Dec
            year = calendarYear;
            if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
          } else { // Jan-Mar
            year = calendarYear - 1;
            if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
          }

          monthIndex = (month + 9) % 12;
          displayYear = year;

          console.log('po - date:', itemDateStr, 'month:', month, 'calendarYear:', calendarYear, 'fyYear:', year, 'displayYear:', displayYear, 'monthIndex:', monthIndex);
        } else {
          // Calendar year logic
          year = date.getFullYear();
          if (year < this.CustomerStartYear! || year > this.CustomerEndYear!) return;
          monthIndex = date.getMonth();
          displayYear = year;
        }

        const month = months[monthIndex];
        const monthYear = `${month} ${displayYear}`;

        if (!revenueByYear.has(year)) {
          revenueByYear.set(year, new Map<string, number>());
          months.forEach(m => revenueByYear.get(year)!.set(m, 0));
        }

        const yearRevenue = revenueByYear.get(year)!;
        const currentValue = yearRevenue.get(month) || 0;
        const newValue = currentValue + (item.totalPrice || 0);
        yearRevenue.set(month, newValue);

        if (newValue > maxValue) {
          maxValue = newValue;
          this.peakMonth = { month: monthYear, value: newValue };
        }
        if (newValue < minValue && newValue > 0) {
          minValue = newValue;
          this.lowestMonth = { month: monthYear, value: newValue };
        } else if (minValue === Infinity && newValue === 0) {
          minValue = 0;
          this.lowestMonth = { month: monthYear, value: newValue };
        }
      }
    });

    // Rest of chart configuration remains the same
    const colors = [
      '#2196F3', '#4CAF50', '#F44336', '#FFC107',
      '#9C27B0', '#00BCD4', '#FF9800', '#795548'
    ];

    const datasets = Array.from(revenueByYear.keys()).sort().map((year: number, index: number) => {
      const yearRevenue = revenueByYear.get(year)!;
      const data = months.map(month => yearRevenue.get(month) || 0);
      const hasNonZeroValues = data.some(value => value > 0);

      if (!hasNonZeroValues) {
        return null;
      }

      const datasetConfig = {
        label: this.selectedIndYearType === 'financial'
          ? `FY ${year}-${(year + 1).toString().substr(2, 2)}`
          : `${year}`,
        data: data,
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length],
        borderWidth: 2,
      };

      if (this.isLineChart) {
        Object.assign(datasetConfig, {
          tension: 0.4,
          fill: false,
          pointBackgroundColor: colors[index % colors.length],
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        });
      } else {
        Object.assign(datasetConfig, {
          barPercentage: 0.4,
          categoryPercentage: 0.8,
          fill: true
        });
      }

      return datasetConfig;
    }).filter(dataset => dataset !== null);

    console.log('Prepared monthlyRevenueData:', this.monthlyRevenueData);

    this.monthlyRevenueData = {
      labels: months,
      datasets,
    };

    this.chartOptions = {
      ...this.chartOptions,
      plugins: {
        ...this.chartOptions.plugins,
        datalabels: {
          anchor: 'end',
          align: 'end',
          formatter: (value: number) => {
            if (value <= 0) {
              return '';
            }
            const thousands = value / 1000;
            let formatted: string;

            if (value < 1000) {
              formatted = thousands.toFixed(2);
            } else if (value < 100000) {
              formatted = thousands.toFixed(1);
            } else {
              formatted = thousands.toFixed(0);
            }

            return `$${formatted}K`;
          },
          rotation: this.isLineChart ? 0 : 90,
          font: { size: 6, family: 'Arial', weight: 'bold' },
        }
      },
      scales: {
        x: {
          ...this.chartOptions.scales.x,
          title: {
            ...this.chartOptions.scales.x.title,
            text: this.selectedIndYearType === 'financial' ? 'Financial Month' : 'Calendar Month',
            font: { size: 14, weight: 'bold', family: 'Arial' }
          },
          ticks: {
            font: { size: 11, family: 'Arial' },
            maxRotation: 45,
            minRotation: 45
          },
          grid: { display: false }
        },
        y: {
          ...this.chartOptions.scales.y,
          title: {
            ...this.chartOptions.scales.y.title,
            text: this.selectedDataType === 'revenue' ? 'Revenue (USD)' :
              this.selectedDataType === 'invoiceValue' ? 'Invoice (USD)' : 'PO (USD)',
            font: { size: 14, weight: 'bold', family: 'Arial' }
          },
          ticks: {
            callback: (value: any) => {
              const thousands = (value / 1000).toFixed(1);
              return `$${thousands}K`;
            },
            font: { size: 11, family: 'Arial' }
          },
          grid: { color: 'rgba(0, 0, 0, 0.1)' }
        }
      },
      elements: {
        ...(this.isLineChart ? {
          line: { tension: 0.4, borderWidth: 3 },
          point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
        } : {
          bar: {
            borderWidth: 1,
            barPercentage: 0.35,
            categoryPercentage: 0.75
          }
        })
      }
    };
  }




  prepareCustomerDistributionChart(): void {
    if (!this.startYear || !this.endYear || this.startYear > this.endYear) {
      this.customerDistributionData = { labels: [], datasets: [] };
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Please select a valid start and end year',
        life: 3000
      });
      return;
    }

    // Map to store original customer names for display
    const originalCustomerNames = new Map<string, string>();
    this.workflowItems.forEach(item => {
      if (item.customerName) {
        const normalized = this.normalizeCustomerName(item.customerName);
        originalCustomerNames.set(normalized, item.customerName);
      }
    });

    // Normalize selected customers
    const normalizedSelectedCustomers = this.selectedCustomers.map(customer =>
      this.normalizeCustomerName(customer)
    );

    // Aggregate data with normalized customer names
    const monthlyRevenueByCustomer = new Map<string, Map<string, number>>();
    this.workflowItems.forEach(item => {
      if (item.customerName) {
        const normalizedCustomer = this.normalizeCustomerName(item.customerName);
        if (normalizedSelectedCustomers.includes(normalizedCustomer)) {
          if (this.selectedDataType === 'revenue') {
            (item.payments || []).forEach((pay: any) => {
              const itemDateStr = pay.paymentReceivedDate;
              if (!itemDateStr) return;

              const date = new Date(itemDateStr);
              if (isNaN(date.getTime())) {
                return;
              }

              const year = date.getFullYear();
              if (year >= this.startYear! && year <= this.endYear!) {
                const month = this.monthNames[date.getMonth()];
                const customerData = monthlyRevenueByCustomer.get(normalizedCustomer) || new Map<string, number>();
                const currentValue = customerData.get(month) || 0;
                const newValue = currentValue + (pay.paymentPrice || 0);
                customerData.set(month, newValue);
                monthlyRevenueByCustomer.set(normalizedCustomer, customerData);
              }
            });
          } else if (this.selectedDataType === 'invoiceValue') {
            (item.invoices || []).forEach((inv: any) => {
              const itemDateStr = inv.date;
              if (!itemDateStr) return;

              const date = new Date(itemDateStr);
              if (isNaN(date.getTime())) {
                return;
              }

              const year = date.getFullYear();
              if (year >= this.startYear! && year <= this.endYear!) {
                const month = this.monthNames[date.getMonth()];
                const customerData = monthlyRevenueByCustomer.get(normalizedCustomer) || new Map<string, number>();
                const currentValue = customerData.get(month) || 0;
                const newValue = currentValue + (inv.invoicePrice || 0);
                customerData.set(month, newValue);
                monthlyRevenueByCustomer.set(normalizedCustomer, customerData);
              }
            });
          } else if (this.selectedDataType === 'poValue') {
            const itemDateStr = item.poDate;
            if (!itemDateStr) return;

            const date = new Date(itemDateStr);
            if (isNaN(date.getTime())) {
              return;
            }

            const year = date.getFullYear();
            if (year >= this.startYear! && year <= this.endYear!) {
              const month = this.monthNames[date.getMonth()];
              const customerData = monthlyRevenueByCustomer.get(normalizedCustomer) || new Map<string, number>();
              const currentValue = customerData.get(month) || 0;
              const newValue = currentValue + (item.totalPrice || 0);
              customerData.set(month, newValue);
              monthlyRevenueByCustomer.set(normalizedCustomer, customerData);
            }
          } else {
            return; // Skip item if invalid type
          }
        }
      }
    });

    const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#00BCD4', '#FF9800', '#795548'];
    const datasets = normalizedSelectedCustomers.map((normalizedCustomer, index) => {
      const originalCustomer = originalCustomerNames.get(normalizedCustomer) || normalizedCustomer;
      const datasetConfig = {
        label: originalCustomer, // Use original name for display
        data: this.monthNames.map(month => monthlyRevenueByCustomer.get(normalizedCustomer)?.get(month) || 0),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        borderWidth: 2,
      };

      if (this.isCustomerLineChart) {
        Object.assign(datasetConfig, {
          tension: 0.4,
          fill: false,
          pointBackgroundColor: colors[index % colors.length],
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        });
      } else {
        Object.assign(datasetConfig, {
          barPercentage: 0.4,
          categoryPercentage: 0.8,
          fill: true
        });
      }

      return datasetConfig;
    });

    this.customerDistributionData = {
      labels: this.monthNames,
      datasets
    };

    this.customerChartOptions = {
      ...this.customerChartOptions,
      plugins: {
        ...this.customerChartOptions.plugins,
        legend: {
          display: false, // Show legend to distinguish customers
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          callbacks: {
            label: (context: any) => {
              const value = context.raw;
              return `${context.dataset.label}: ${value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`;
            }
          }
        },
        datalabels: {
          anchor: 'end', // Position at the end of bars
          align: 'end', // Place labels above bars
          formatter: (value: number) => {
            if (value <= 0) {
              return '';  // Hide labels for zero or negative values
            }
            const thousand = value / 1000;
            let formatted: string;

            if (value < 1000) {
              formatted = thousand.toFixed(1);  // e.g., 500 → $0.00M
            } else if (value < 100000) {
              formatted = thousand.toFixed(0);  // e.g., 50000 → $0.05M (using 2 decimals for precision)
            } else if (value < 1000000) {
              formatted = thousand.toFixed(0);  // e.g., 500000 → $0.5M
            } else {
              formatted = thousand.toFixed(0);  // e.g., 1500000 → $2M (no decimals for large values)
            }

            return `$${formatted}`;
          },
          color: '#000', // Text color for labels
          font: { size: 6, weight: 'bold' },
          rotation: this.isLineChart ? 0 : -90 // Rotate labels for bar chart to fit better
        }
      },
      scales: {
        x: {
          ...this.customerChartOptions?.scales?.x,
          title: {
            ...this.customerChartOptions?.scales?.x?.title,
            display: true,
            text: 'Month',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        },
        y: {
          ...this.customerChartOptions?.scales?.y,
          beginAtZero: true,
          title: {
            ...this.customerChartOptions?.scales?.y?.title,
            display: true,
            text: this.selectedDataType === 'revenue' ? 'Revenue (USD)' :
              this.selectedDataType === 'invoiceValue' ? 'Invoice (USD)' : 'PO (USD)',
          },
          rotation: this.isLineChart ? 0 : 90, // Rotate labels to match x-axis
          font: { size: 10, family: 'Arial', weight: 'bold' },
          ticks: {
            callback: (value: any) => {
              const millions = (value / 1000).toFixed(1);
              return `$${millions}K`;
            },
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        ...(this.isCustomerLineChart ? {
          line: { tension: 0.4, borderWidth: 3 },
          point: { radius: 4, hitRadius: 8, hoverRadius: 6 }
        } : {
          bar: { borderWidth: 1 }
        })
      }
    };
  }


  prepareYearlyComparisonChart(): void {
  const yearlyQuotationAmount = new Map<number, number>();
  const yearlyPoAmount = new Map<number, number>();
  const yearlyInvoiceAmount = new Map<number, number>();
  const yearlyPaymentAmount = new Map<number, number>();
  const processedQuotationNos = new Set<string>();
  const allYears = new Set<number>();

  // Process each unique quotation item
  this.workflowItems.forEach(item => {
    if (!processedQuotationNos.has(item.quotationNo)) {
      processedQuotationNos.add(item.quotationNo);

      // Quotations: Sum totalPrice by quotationDate year
      if (item.quotationDate) {
        const year = new Date(item.quotationDate).getFullYear();
        allYears.add(year);
        const current = yearlyQuotationAmount.get(year) || 0;
        yearlyQuotationAmount.set(year, current + (item.totalPrice || 0));
      }

      // POs: Sum totalPrice by poDate year, only if poNumber exists and poDate valid
      if (item.poDate) {
        const poDate = new Date(item.poDate);
        if (!isNaN(poDate.getTime())) {
          const year = poDate.getFullYear();
          allYears.add(year);
          const current = yearlyPoAmount.get(year) || 0;
          yearlyPoAmount.set(year, current + (item.totalPrice || 0));
        }
      }

      // Invoices: Sum invoicePrice by invoice.date year
      if (item.invoices && item.invoices.length > 0) {
        item.invoices.forEach((inv: any) => {
          const invDate = new Date(inv.date);
          if (!isNaN(invDate.getTime())) {
            const year = invDate.getFullYear();
            allYears.add(year);
            const current = yearlyInvoiceAmount.get(year) || 0;
            yearlyInvoiceAmount.set(year, current + (inv.invoicePrice || 0));
          }
        });
      }

      // Payments: Sum paymentPrice by paymentReceivedDate year
      if (item.payments && item.payments.length > 0) {
        item.payments.forEach((pay: any) => {
          const payDate = new Date(pay.paymentReceivedDate);
          if (!isNaN(payDate.getTime())) {
            const year = payDate.getFullYear();
            allYears.add(year);
            const current = yearlyPaymentAmount.get(year) || 0;
            yearlyPaymentAmount.set(year, current + (pay.paymentPrice || 0));
          }
        });
      }
    }
  });

  const sortedYears = Array.from(allYears).sort((a, b) => a - b);

  // Prepare datasets for bar chart (side‑by‑side bars per year)
  this.yearlyAmountsData = {
    labels: sortedYears.map(year => year.toString()),
    datasets: [
      {
        label: 'Quotations',
        data: sortedYears.map(year => yearlyQuotationAmount.get(year) || 0),
        backgroundColor: 'rgba(52, 73, 94, 0.8)',
        borderColor: '#34495E',
        borderWidth: 1
        // stack property removed
      },
      {
        label: 'POs',
        data: sortedYears.map(year => yearlyPoAmount.get(year) || 0),
        backgroundColor: 'rgba(26, 188, 156, 0.8)',
        borderColor: '#1ABC9C',
        borderWidth: 1
        // stack property removed
      },
      {
        label: 'Invoices',
        data: sortedYears.map(year => yearlyInvoiceAmount.get(year) || 0),
        backgroundColor: 'rgba(243, 156, 18, 0.8)',
        borderColor: '#F39C12',
        borderWidth: 1
        // stack property removed
      },
      {
        label: 'Payments (Revenue)',
        data: sortedYears.map(year => yearlyPaymentAmount.get(year) || 0),
        backgroundColor: 'rgba(39, 174, 96, 0.8)',
        borderColor: '#27AE60',
        borderWidth: 1
        // stack property removed
      }
    ]
  };

  // Chart options: side‑by‑side (stacked: false), USD formatting
  this.yearlyAmountsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,          // <--- changed from true to false
        title: {
          display: true,
          text: 'Years',
          align: 'center' as const,
          font: {
            size: 12,
            weight: 'bold' as const
          },
          color: '#666',
          padding: {
            bottom: 10
          }
        },
        ticks: {
          font: { size: 10 },
          maxRotation: 0
        },
        grid: {
          display: false
        }
      },
      y: {
        stacked: false,          // <--- changed from true to false
        title: {
          display: true,
          text: 'Amount',
          align: 'center' as const,
          font: {
            size: 12,
            weight: 'bold' as const,
            family: 'Arial'
          },
          color: '#666',
          padding: {
            right: 10
          }
        },
        beginAtZero: true,
        ticks: {
          callback: (value: number): string => {
            if (value >= 1000) {
              if (value >= 1000000) {
                return `$${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
              } else {
                return `$${(value / 1000).toFixed(1)}K`;
              }
            } else {
              return `$${value.toLocaleString()}`;
            }
          },
          font: { size: 10 },
          stepSize: 50000
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
        // maxBarThickness and minBarLength moved to elements.bar (below)
      }
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: { size: 10 },
          padding: 15,
          usePointStyle: true
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context: Array<TooltipItem<'bar'>>): string => {
            return `Year: ${context[0].label}`;
          },
          label: (context: TooltipItem<'bar'>): string => {
            const value = context.parsed.y as number;
            const label = context.dataset.label || '';
            let formattedValue: string;

            if (value >= 1000) {
              if (value >= 1000000) {
                formattedValue = `$${(value / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
              } else {
                formattedValue = `$${(value / 1000).toFixed(1)}K`;
              }
            } else {
              formattedValue = `$${value.toLocaleString()}`;
            }

            return `${label}: ${formattedValue}`;
          },
          footer: (tooltipItems: Array<TooltipItem<'bar'>>): string => {
            const total = tooltipItems.reduce((sum, item) => {
              return sum + (item.parsed.y as number);
            }, 0);

            let totalFormatted: string;
            if (total >= 1000) {
              if (total >= 1000000) {
                totalFormatted = `$${(total / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
              } else {
                totalFormatted = `$${(total / 1000).toFixed(1)}K`;
              }
            } else {
              totalFormatted = `$${total.toLocaleString()}`;
            }

            return `Total: ${totalFormatted}`;
          }
        },
        displayColors: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 12, weight: 'bold' },
        bodyFont: { size: 11 },
        footerFont: { size: 11, weight: 'bold' },
        cornerRadius: 6,
        padding: 12
      },
      datalabels: {
        formatter: (value: number) => {
          if (value <= 0) return '';
          const thousand = value / 1000;
          let formatted: string;
          if (value < 1000) {
            formatted = thousand.toFixed(2);
          } else if (value < 100000) {
            formatted = thousand.toFixed(0);
          } else if (value < 1000000) {
            formatted = thousand.toFixed(0);
          } else {
            formatted = thousand.toFixed(0);
          }
          return `$${formatted}`;
        },
        color: '#000',
        font: { size: 10, weight: 'bold' },
        padding: 4
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    elements: {
      bar: {
        borderSkipped: false,
        borderRadius: 4,
        maxBarThickness: 60,    // moved from y scale
        minBarLength: 2         // moved from y scale
      }
    }
  };

  // Optional: Calculate overall yearly growth for Quotations
  if (sortedYears.length >= 2) {
    const currentYear = sortedYears[sortedYears.length - 1];
    const prevYear = sortedYears[sortedYears.length - 2];
    const currentValue = yearlyQuotationAmount.get(currentYear) || 0;
    const prevValue = yearlyQuotationAmount.get(prevYear) || 0;
    this.yearlyGrowth = prevValue > 0 ? ((currentValue - prevValue) / prevValue) * 100 : 0;
  } else {
    this.yearlyGrowth = 0;
  }
}

  updateMonthlyRevenueChart(): void {
    if (this.CustomerStartYear && this.CustomerEndYear && this.CustomerStartYear > this.CustomerEndYear) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Start year cannot be greater than end year',
        life: 3000
      });
      this.CustomerEndYear = this.CustomerStartYear;
    }
    this.prepareMonthlyRevenueChart();
  }


  updateCustomerChart(): void {
    if (this.startYear && this.endYear && this.startYear > this.endYear) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Start year cannot be greater than end year',
        life: 3000
      });
      this.endYear = this.startYear;
    }
    this.prepareCustomerDistributionChart();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  getPaymentRatio(): number {
    return this.totalQuarter > 0 ? (this.totalRavenue / this.totalQuarter) * 100 : 0;
  }

  formatFinancialYearRange(yearOption: YearOption): string {
    if (!yearOption) return '';
    return `${this.formatDate(yearOption.value.start)} - ${this.formatDate(yearOption.value.end)}`;
  }

  formatCalendarYearRange(yearOption: YearOption): string {
    if (!yearOption) return '';
    return `${this.formatDate(yearOption.value.start)} - ${this.formatDate(yearOption.value.end)}`;
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  }

  applyTableFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.toLowerCase();
    this.filteredItems = this.workflowItems.filter(item =>
      item.quotationNo.toLowerCase().includes(value) ||
      item.customerName.toLowerCase().includes(value) ||
      item.project.toLowerCase().includes(value) ||  // Updated from projectName
      (item.invoices || []).some((inv: any) => inv.invoiceNo.toLowerCase().includes(value)) ||
      (item.payments || []).some((pay: any) => pay.invoiceNo.toLowerCase().includes(value))
    );
    this.calculateMetrics();
    this.prepareChartData();
  }


  exportToCSV(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Export Started',
      detail: 'CSV export will begin shortly',
      life: 3000
    });
  }

  viewQuotationDetails(item: QuotationItem): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Quotation Selected',
      detail: `Viewing details for ${item.quotationNo}`,
      life: 3000
    });
  }

  refreshData(): void {
    this.loading = true;
    setTimeout(() => {
      this.loadData();
      this.messageService.add({
        severity: 'success',
        summary: 'Data Refreshed',
        detail: 'Dashboard data has been updated',
        life: 3000
      });
    }, 1000);
  }
  updateTotalSalesChart(): void {
    if (this.totalSalesStartYear && this.totalSalesEndYear && this.totalSalesStartYear > this.totalSalesEndYear) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Start year cannot be greater than end year',
        life: 3000
      });
      this.totalSalesEndYear = this.totalSalesStartYear;
    }
    this.prepareTotalSalesChart();
  }

  toggleTotalSalesChartType(): void {
    this.isTotalSalesLineChart = !this.isTotalSalesLineChart;
    this.totalSalesChartType = this.isTotalSalesLineChart ? 'line' : 'bar';
    this.prepareTotalSalesChart();
  }
  toggleAccuChartType(): void {
    this.isAccumulativeLineChart = !this.isAccumulativeLineChart;
    this.annualSalesChartType = this.isAccumulativeLineChart ? 'line' : 'bar';

    console.log(`Toggling chart type to: ${this.annualSalesChartType}`);

    // Update chart immediately if data is ready
    if (this.selectedTotalAnnualSalesYear) {
      this.updateAnnualSalesChart();
    }
  }


  prepareTotalSalesChart(): void {
    if (!this.totalSalesStartYear || !this.totalSalesEndYear || this.totalSalesStartYear > this.totalSalesEndYear) {
      this.totalSalesData = { labels: [], datasets: [] };
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Date Range',
        detail: 'Please select a valid start and end year',
        life: 3000
      });
      return;
    }

    // Initialize data structures
    const salesByYear = new Map<number, number>();
    // Generate year labels for x-axis
    const years: number[] = [];
    for (let year = this.totalSalesStartYear; year <= this.totalSalesEndYear; year++) {
      years.push(year);
      salesByYear.set(year, 0);
    }

    // Define color palette for bars
    const colorPalette = [
      '#2196F3', '#FF5722', '#4CAF50', '#F44336', '#673AB7',
      '#FFC107', '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5'
    ];

    // Normalize selected customer name
    const normalizedSelectedCustomer = this.normalizeCustomerName(this.selectedTotalSalesCustomer);

    // Filter items based on customer and relevant fields
    const filteredItems = this.workflowItems.filter(item => {
      // Customer filter
      const customerMatch = this.selectedTotalSalesCustomer === 'all' ||
        this.normalizeCustomerName(item.customerName) === normalizedSelectedCustomer;
      if (!customerMatch) return false;

      // At least one relevant date field
      if (this.selectedDataType === 'revenue') {
        const paymentDates = (item.payments || []).map((pay: any) => pay.paymentReceivedDate).filter(Boolean);
        return paymentDates.length > 0;
      } else if (this.selectedDataType === 'invoiceValue') {
        const invoiceDates = (item.invoices || []).map((inv: any) => inv.date).filter(Boolean);
        return invoiceDates.length > 0;
      } else if (this.selectedDataType === 'poValue') {
        return item.poDate;
      }
      return false;
    });

    // Aggregate data into salesByYear, using individual payment/invoice/PO dates for accuracy
    filteredItems.forEach(item => {
      if (this.selectedDataType === 'revenue') {
        (item.payments || []).forEach((pay: any) => {
          const dateStr = pay.paymentReceivedDate;
          if (!dateStr) return;
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return;
          let year = date.getFullYear();
          if (this.selectedTotalSalesYearType === 'financial' && date.getMonth() >= 3) year += 1;
          if (year >= this.totalSalesStartYear! && year <= this.totalSalesEndYear!) {
            const prev = salesByYear.get(year) || 0;
            salesByYear.set(year, prev + (pay.paymentPrice || 0));
          }
        });
      } else if (this.selectedDataType === 'invoiceValue') {
        (item.invoices || []).forEach((inv: any) => {
          const dateStr = inv.date;
          if (!dateStr) return;
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return;
          let year = date.getFullYear();
          if (this.selectedTotalSalesYearType === 'financial' && date.getMonth() >= 3) year += 1;
          if (year >= this.totalSalesStartYear! && year <= this.totalSalesEndYear!) {
            const prev = salesByYear.get(year) || 0;
            salesByYear.set(year, prev + (inv.invoicePrice || 0));
          }
        });
      } else if (this.selectedDataType === 'poValue') {
        const dateStr = item.poDate;
        if (!dateStr) return;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return;
        let year = date.getFullYear();
        if (this.selectedTotalSalesYearType === 'financial' && date.getMonth() >= 3) year += 1;
        if (year >= this.totalSalesStartYear! && year <= this.totalSalesEndYear!) {
          const prev = salesByYear.get(year) || 0;
          salesByYear.set(year, prev + (item.totalPrice || 0));
        }
      }
    });

    const data = years.map(year => salesByYear.get(year) || 0);
    const hasNonZeroValues = data.some(value => value > 0);

    const backgroundColors = years.map((_, index) => colorPalette[index % colorPalette.length]);
    const borderColors = backgroundColors;

    const datasetConfig = {
      label: this.selectedTotalSalesYearType === 'financial'
        ? `Financial Years ${this.totalSalesStartYear}-${this.totalSalesEndYear}`
        : `Years ${this.totalSalesStartYear}-${this.totalSalesEndYear}`,
      data: data,
      backgroundColor: this.isTotalSalesLineChart ? backgroundColors[0] : backgroundColors,
      borderColor: this.isTotalSalesLineChart ? borderColors[0] : borderColors,
      borderWidth: 2,
    };

    if (this.isTotalSalesLineChart) {
      Object.assign(datasetConfig, {
        tension: 0.4,
        fill: false,
        pointBackgroundColor: backgroundColors[0],
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      });
    } else {
      Object.assign(datasetConfig, {
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        fill: true
      });
    }

    this.totalSalesData = {
      labels: years.map(year => {
        if (this.selectedTotalSalesYearType === 'financial') {
          return `Apr ${year - 1} - Mar ${year}`;
        } else {
          return `${year}`;
        }
      }),
      datasets: hasNonZeroValues ? [datasetConfig] : []
    };

    this.totalSalesChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          callbacks: {
            label: (context: any) => {
              return `${context.dataset.label}: ${this.formatCurrency(context.raw)}`;
            }
          }
        },
        datalabels: {
          anchor: 'end',
          align: 'top',
          formatter: (value: number) => {
            if (value <= 0) return '';
            const thousand = value / 1000;
            let formatted: string;
            if (value < 1000) {
              formatted = thousand.toFixed(2);
            } else if (value < 100000) {
              formatted = thousand.toFixed(0);
            } else if (value < 1000000) {
              formatted = thousand.toFixed(0);
            } else {
              formatted = thousand.toFixed(0);
            }
            return `$${formatted}`;
          },
          color: '#000',
          font: { size: 12, weight: 'bold' },
          padding: 4
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.selectedTotalSalesYearType === 'financial' ? 'Financial Years' : 'Years',
            font: { weight: 'bold' }
          },
          ticks: { font: { size: 11 } },
          grid: { display: false }
        },
        y: {
          beginAtZero: true,
          suggestedMin: 0,
          suggestedMax: 2000000,
          title: {
            display: true,
            text: 'Amount (thousand $)',
            font: { weight: 'bold' }
          },
          ticks: {
            callback: (value: any) => {
              const thousand = (value / 1000).toFixed(0);
              return `$${thousand}K`;
            },
            font: { size: 11 },
            stepSize: 200000
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' }
        }
      },
      interaction: { intersect: false, mode: 'index' },
      elements: {
        bar: { borderWidth: 2 }
      }
    };
  }


  prepareAnnualSalesChart(): void {
    // Null check for selected year
    if (!this.selectedTotalAnnualSalesYear || this.selectedTotalAnnualSalesYear === null) {
      this.annualSalesData = { labels: [], datasets: [] };
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Selection',
        detail: 'Please select a year',
        life: 3000
      });
      return;
    }

    // Extract selected values
    const selectedYear = this.selectedTotalAnnualSalesYear;
    const normalizedSelectedCustomer = this.selectedTotalAnnualSalesCustomer === 'all'
      ? 'all'
      : this.normalizeCustomerName(this.selectedTotalAnnualSalesCustomer);
    const isLineChart = this.annualSalesChartType === 'line';
    const isStackedBar = !isLineChart; // Always stacked for bar charts
    const useCumulative = isLineChart || isStackedBar; // Cumulative for both line and bar charts

    console.log(`Preparing chart - Year: ${selectedYear}, Type: ${this.selectedTotalAnnualSalesYearType}, Chart: ${isLineChart ? 'Line' : 'Cumulative Stacked Bar'}, Data: ${this.selectedDataType}`);

    // Get current date for month limiting
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // January = 1

    // Generate month labels based on year type and limit to current month
    let months: string[] = [];

    if (this.selectedTotalAnnualSalesYearType === 'financial') {
      // Financial year: Apr 2025 - Mar 2026 for selectedYear 2025
      const financialMonths = [
        `Apr ${selectedYear}`, `May ${selectedYear}`, `Jun ${selectedYear}`, `Jul ${selectedYear}`,
        `Aug ${selectedYear}`, `Sep ${selectedYear}`, `Oct ${selectedYear}`, `Nov ${selectedYear}`,
        `Dec ${selectedYear}`, `Jan ${selectedYear + 1}`, `Feb ${selectedYear + 1}`, `Mar ${selectedYear + 1}`
      ];

      // For financial year, determine how many months to show
      // If current year is same as selected or next year, show up to current month
      let monthLimit = financialMonths.length; // Default to full year

      if (currentYear === selectedYear) {
        // Current year is selected year - show Apr to current month
        const financialMonthMap: { [key: string]: number } = {
          'Apr': 4, 'May': 5, 'Jun': 6, 'Jul': 7,
          'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
        };

        for (let i = 0; i < 9; i++) { // First 9 months are in selected year (Apr-Dec)
          const monthName = financialMonths[i].split(' ')[0];
          const monthNum = financialMonthMap[monthName];

          if (monthNum >= currentMonth) {
            monthLimit = i;
            break;
          }
        }
      } else if (currentYear === selectedYear + 1) {
        // Current year is the next year - show full year up to current month
        const nextYearMonths = [10, 11, 12]; // Jan, Feb, Mar indices in financial array
        const financialMonthMap: { [key: string]: number } = {
          'Jan': 1, 'Feb': 2, 'Mar': 3
        };

        // Show all first 9 months (Apr-Dec previous year) + up to current month
        let totalMonths = 9;
        for (let i = 0; i < 3; i++) { // Check Jan-Mar
          const monthName = financialMonths[9 + i].split(' ')[0];
          const monthNum = financialMonthMap[monthName];

          if (monthNum > currentMonth) {
            break;
          }
          totalMonths++;
        }
        monthLimit = totalMonths;
      }

      months = financialMonths.slice(0, monthLimit);

    } else {
      // Calendar year: Jan-Dec same year
      const calendarMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .map(month => `${month} ${selectedYear}`);

      // Limit to current month for calendar year
      const monthLimit = currentYear === selectedYear ? currentMonth : 12;
      months = calendarMonths.slice(0, monthLimit);
    }

    console.log(`Generated ${months.length} months for ${this.selectedTotalAnnualSalesYearType} year:`, months);

    // Initialize maps for monthly data
    const monthlyWorkflowSales = new Map<string, number>();
    const monthlyKikF1Sales = new Map<string, number>();

    // Initialize all months to 0
    months.forEach(month => {
      monthlyWorkflowSales.set(month, 0);
      monthlyKikF1Sales.set(month, 0);
    });

    let totalWorkflowSales = 0;
    let totalKikF1Sales = 0;

    // Step 1: Filter and aggregate workflow items (revenue/invoices/POs) - only for shown months
    const filteredItems = this.workflowItems.filter(item => {
      // Customer filter
      const customerMatch = this.selectedTotalAnnualSalesCustomer === 'all' ||
        this.normalizeCustomerName(item.customerName) === normalizedSelectedCustomer;
      if (!customerMatch) return false;

      // Data type specific date checks - only include items within the date range
      let itemDate: Date | null = null;

      if (this.selectedDataType === 'revenue') {
        const paymentDates = (item.payments || []).map((pay: any) => pay.paymentReceivedDate).filter(Boolean);
        if (paymentDates.length === 0) return false;
        // Use the earliest payment date in the selected period
        itemDate = new Date(Math.min(...paymentDates.map(d => new Date(d).getTime())));
      } else if (this.selectedDataType === 'invoiceValue') {
        const invoiceDates = (item.invoices || []).map((inv: any) => inv.date).filter(Boolean);
        if (invoiceDates.length === 0) return false;
        // Use the earliest invoice date in the selected period
        itemDate = new Date(Math.min(...invoiceDates.map(d => new Date(d).getTime())));
      } else if (this.selectedDataType === 'poValue') {
        if (!item.poDate) return false;
        itemDate = new Date(item.poDate);
      }

      if (!itemDate || isNaN(itemDate.getTime())) return false;

      // Check if item date falls within the selected period up to current date
      const { isValidDate } = this.getMonthLabelForDate(
        itemDate, selectedYear, this.selectedTotalAnnualSalesYearType, months
      );

      return isValidDate;
    });

    console.log(`Filtered ${filteredItems.length} workflow items for ${this.selectedDataType}`);

    // Step 2: Aggregate workflow data by data type - only for shown months
    if (this.selectedDataType === 'revenue') {
      // Aggregate payments
      filteredItems.forEach(item => {
        (item.payments || []).forEach((pay: any) => {
          const dateStr = pay.paymentReceivedDate;
          if (!dateStr) return;

          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return;

          const { monthLabel, isValidDate } = this.getMonthLabelForDate(
            date, selectedYear, this.selectedTotalAnnualSalesYearType, months
          );

          if (isValidDate && monthLabel && months.includes(monthLabel)) {
            const prev = monthlyWorkflowSales.get(monthLabel) || 0;
            const toAdd = pay.paymentPrice || 0;
            monthlyWorkflowSales.set(monthLabel, prev + toAdd);
            totalWorkflowSales += toAdd;
          }
        });
      });
    } else if (this.selectedDataType === 'invoiceValue') {
      // Aggregate invoices
      filteredItems.forEach(item => {
        (item.invoices || []).forEach((inv: any) => {
          const dateStr = inv.date;
          if (!dateStr) return;

          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return;

          const { monthLabel, isValidDate } = this.getMonthLabelForDate(
            date, selectedYear, this.selectedTotalAnnualSalesYearType, months
          );

          if (isValidDate && monthLabel && months.includes(monthLabel)) {
            const prev = monthlyWorkflowSales.get(monthLabel) || 0;
            const toAdd = inv.invoicePrice || 0;
            monthlyWorkflowSales.set(monthLabel, prev + toAdd);
            totalWorkflowSales += toAdd;
          }
        });
      });
    } else if (this.selectedDataType === 'poValue') {
      // Aggregate PO values
      filteredItems.forEach(item => {
        const dateStr = item.poDate;
        if (!dateStr) return;

        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return;

        const { monthLabel, isValidDate } = this.getMonthLabelForDate(
          date, selectedYear, this.selectedTotalAnnualSalesYearType, months
        );

        if (isValidDate && monthLabel && months.includes(monthLabel)) {
          const prev = monthlyWorkflowSales.get(monthLabel) || 0;
          const toAdd = item.totalPrice || 0;
          monthlyWorkflowSales.set(monthLabel, prev + toAdd);
          totalWorkflowSales += toAdd;
        }
      });
    }

    // Step 3: Aggregate KIK#F1 data for selected period - only for shown months
    if (this.kikF1MonthlyTotals.size > 0) {
      console.log('Processing KIK#F1 data for aggregation...');

      this.kikF1MonthlyTotals.forEach((value, key) => {
        const [yearStr, monthStr, customerKey] = key.split('-');
        const kikYear = parseInt(yearStr);
        const monthIndex = parseInt(monthStr);

        // Skip invalid data
        if (isNaN(kikYear) || isNaN(monthIndex)) return;

        // Customer filter
        if (normalizedSelectedCustomer !== 'all' && customerKey !== normalizedSelectedCustomer) {
          return;
        }

        // Find matching month label within the limited months
        const monthLabel = this.findMonthLabelForKikF1(
          monthIndex, kikYear, selectedYear, this.selectedTotalAnnualSalesYearType, months
        );

        if (monthLabel && months.includes(monthLabel)) {
          const current = monthlyKikF1Sales.get(monthLabel) || 0;
          monthlyKikF1Sales.set(monthLabel, current + value);
          totalKikF1Sales += value;
          console.log(`KIK#F1 Added to ${monthLabel}: +$${value.toLocaleString()}`);
        }
      });

      console.log('KIK#F1 Monthly Totals:', Array.from(monthlyKikF1Sales.entries()));
    }

    // Step 4: Calculate cumulative data for shown months only
    const workflowCumulativeData: number[] = [];
    const kikF1CumulativeData: number[] = [];
    const combinedCumulativeData: number[] = [];

    let workflowRunningTotal = 0;
    let kikF1RunningTotal = 0;
    let combinedRunningTotal = 0;

    months.forEach(month => {
      const workflowMonthly = monthlyWorkflowSales.get(month) || 0;
      const kikMonthly = monthlyKikF1Sales.get(month) || 0;

      // Workflow cumulative
      workflowRunningTotal += workflowMonthly;
      workflowCumulativeData.push(workflowRunningTotal);

      // KIK#F1 cumulative
      kikF1RunningTotal += kikMonthly;
      kikF1CumulativeData.push(kikF1RunningTotal);

      // Combined cumulative (for line chart)
      combinedRunningTotal += (workflowMonthly + kikMonthly);
      combinedCumulativeData.push(combinedRunningTotal);
    });

    // For line chart: show workflow vs combined
    // For bar chart: show workflow cumulative vs KIK#F1 cumulative (stacked)
    const workflowChartData = isLineChart ? workflowCumulativeData : workflowCumulativeData;
    const kikF1ChartData = isLineChart ? combinedCumulativeData : kikF1CumulativeData;

    // Step 5: Calculate max value for chart scaling based on shown data
    const allValues = [...workflowChartData, ...kikF1ChartData];
    const maxValue = Math.max(...allValues.filter(v => v > 0), 0);
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 100000;

    // Step 6: Create datasets based on chart type
    const getDataLabel = (value: number): string => {
      if (value === 0) return '';

      const thousand = value / 1000;
      if (thousand >= 1000) {
        return `$${(thousand / 1000).toFixed(0)}M`;
      } else if (value >= 1000) {
        return `$${thousand.toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    };

    const workflowDataset = {
      type: isLineChart ? 'line' : 'bar' as const,
      label: isLineChart
        ? `Cumulative Workflow Only (${months.length} months)`
        : `Cumulative Workflow (${months.length} months)`, // Show month count
      data: workflowChartData,
      backgroundColor: isLineChart
        ? 'rgba(76, 175, 80, 0.2)'  // Light green fill for line
        : 'rgba(76, 175, 80, 0.8)', // Semi-transparent green for stacked bar
      borderColor: '#4CAF50',
      borderWidth: isLineChart ? 3 : 1,
      stack: isStackedBar ? 'stack1' : undefined, // Stack identifier for stacked bars
      tension: isLineChart ? 0.4 : undefined,
      fill: isLineChart ? true : false,
      pointBackgroundColor: isLineChart ? '#4CAF50' : undefined,
      pointBorderColor: isLineChart ? '#fff' : undefined,
      pointBorderWidth: isLineChart ? 2 : undefined,
      pointRadius: isLineChart ? 5 : 0,
      pointHoverRadius: isLineChart ? 7 : undefined,
      barThickness: isStackedBar ? 40 : undefined,
      categoryPercentage: isStackedBar ? 0.8 : undefined,
      barPercentage: isStackedBar ? 0.9 : undefined,
      yAxisID: 'y'
    };

    const kikF1Dataset = {
      type: isLineChart ? 'line' : 'bar' as const,
      label: isLineChart
        ? `Cumulative Workflow + KIK#F1 (${months.length} months)`
        : `Cumulative KIK#F1 (${months.length} months)`, // Show month count
      data: kikF1ChartData,
      backgroundColor: isLineChart
        ? 'rgba(33, 150, 243, 0.2)'  // Light blue fill for line
        : 'rgba(33, 150, 243, 0.8)', // Semi-transparent blue for stacked bar
      borderColor: '#2196F3',
      borderWidth: isLineChart ? 3 : 1,
      stack: isStackedBar ? 'stack1' : undefined, // Same stack as workflow for stacking
      tension: isLineChart ? 0.4 : undefined,
      fill: isLineChart ? false : false,
      pointBackgroundColor: isLineChart ? '#2196F3' : undefined,
      pointBorderColor: isLineChart ? '#fff' : undefined,
      pointBorderWidth: isLineChart ? 2 : undefined,
      pointRadius: isLineChart ? 5 : 0,
      pointHoverRadius: isLineChart ? 7 : undefined,
      barThickness: isStackedBar ? 40 : undefined,
      categoryPercentage: isStackedBar ? 0.8 : undefined,
      barPercentage: isStackedBar ? 0.9 : undefined,
      yAxisID: 'y'
    };

    // Step 7: Update chart data
    this.annualSalesData = {
      labels: months,
      datasets: [workflowDataset, kikF1Dataset]
    };

    // Step 8: Configure chart options with cumulative settings and blue text for every month
    const stepSize = Math.max(50000, yAxisMax / 8); // Dynamic step size

    this.annualSalesChartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      interaction: {
        intersect: isLineChart ? false : true,
        mode: isLineChart ? 'index' : 'nearest'
      },
      plugins: {
        legend: {
          display: false,
          position: 'top' as const,
          align: isLineChart ? 'start' : 'center',
          labels: {
            usePointStyle: isLineChart,
            padding: 15,
            font: { size: 12, weight: 'bold' },
            boxWidth: isLineChart ? 20 : 40,
            boxHeight: isLineChart ? 3 : 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 6,
          titleFont: { size: 14, weight: 'bold' },
          bodyFont: { size: 12 },
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context: any[]) => {
              const monthLabel = context[0].label;
              const periodType = this.selectedTotalAnnualSalesYearType === 'financial' ? 'FY' : 'CY';
              return [`${monthLabel} ${periodType} ${selectedYear} (${months.length} months)`];
            },
            label: (context: any) => {
              const value = context.raw as number;
              if (value === 0) return `${context.dataset.label}: No Data`;

              const formatted = value.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0
              });

              // For stacked bars, show breakdown of cumulative totals
              if (isStackedBar) {
                const month = months[context.dataIndex];
                const otherDatasetIndex = context.datasetIndex === 0 ? 1 : 0;
                const otherValue = this.annualSalesData.datasets[otherDatasetIndex].data[context.dataIndex] as number;

                if (otherValue > 0) {
                  const otherFormatted = otherValue.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0
                  });
                  const total = value + otherValue;
                  const totalFormatted = total.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0
                  });

                  return [
                    `${context.dataset.label}: ${formatted}`,
                    `Total Cumulative for ${month}: ${totalFormatted}`
                  ];
                }
              }

              return `${context.dataset.label}: ${formatted}`;
            },
            footer: (context: any[]) => {
              // Always show annual totals for cumulative charts (up to current period)
              const periodText = this.selectedTotalAnnualSalesYearType === 'financial'
                ? `FY ${selectedYear} (${months.length}/12 months)`
                : `CY ${selectedYear} (${months.length}/12 months)`;

              const workflowTotal = totalWorkflowSales.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              });
              const kikTotal = totalKikF1Sales.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              });
              const combinedTotal = (totalWorkflowSales + totalKikF1Sales).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              });

              return;
            }
          }
        },
        datalabels: {
          // Show blue labels for KIK#F1 dataset on EVERY month
          display: (context: any) => {
            // Only show data labels for the KIK#F1 dataset (second dataset, index 1)
            if (context.datasetIndex !== 1) return false;

            const value = context.dataset.data[context.dataIndex] as number;
            // Show label for EVERY month as long as there's a value > 0
            return value > 0;
          },
          anchor: isLineChart ? 'end' : 'end',
          align: isLineChart ? 'top' : (context: any) => {
            // For stacked bars, position based on which segment (KIK#F1 is bottom for stacked)
            if (isStackedBar) {
              return 'center'; // Center align for KIK#F1 segment
            }
            return 'top';
          },
          offset: isLineChart ? 6 : 4,
          // Cumulative data label formatter - text only for KIK#F1
          formatter: (value: number, context: any) => {
            if (value <= 0) return '';

            const thousand = value / 1000;

            // Always show value in 'K'
            return `${thousand.toFixed(
              thousand >= 10 ? 0 : 1  // No decimal if 10K+, one decimal if below
            )}`;
          },

          // Blue color only for KIK#F1 dataset
          color: (context: any) => {
            // Only show blue color for KIK#F1 (datasetIndex 1)
            return context.datasetIndex === 1 ? '#232324ff' : '#232324ff';
          },
          font: {
            size: isLineChart ? 10 : 11, // Slightly larger for cumulative bars
            weight: 'bold'
          },
          // Remove background - set to null for no fill
          backgroundColor: null,
          // Remove border - set to null/transparent
          borderColor: null,
          borderWidth: 0,
          // No padding needed for plain text
          padding: 0,
          // Disable stroke for clean text
          textStrokeColor: 'transparent',
          textStrokeWidth: 0,
          // No rounding or clamping needed for simple text
          borderRadius: 0,
          clamp: false
        }
      },
      scales: {
        x: {
          stacked: isStackedBar,
          title: {
            display: true,
            text: `${useCumulative ? 'Cumulative ' : 'Monthly '} ${this.selectedTotalAnnualSalesYearType} ${selectedYear} (${months.length} months)`,
            font: { size: 12, weight: 'bold' },
            color: '#333',
            padding: { bottom: isLineChart ? 15 : 10 }
          },
          ticks: {
            font: { size: 11 },
            maxRotation: this.selectedTotalAnnualSalesYearType === 'financial' && !isLineChart ? 45 : 0,
            minRotation: 0,
            padding: 8,
            // Shorten month labels for bars
            callback: (value: any, index: number) => {
              if (!isLineChart && this.selectedTotalAnnualSalesYearType === 'financial') {
                const shortMonths = ['A', 'M', 'Jn', 'Jl', 'A', 'S', 'O', 'N', 'D', 'Jn', 'F', 'M'];
                return shortMonths[index] || months[index]?.substring(0, 3) || '';
              }
              return months[index] || value;
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.06)',
            lineWidth: 1,
            drawBorder: true,
            display: !isLineChart
          }
        },
        y: {
          stacked: isStackedBar,
          beginAtZero: true,
          max: yAxisMax,
          title: {
            display: true,
            text: useCumulative ? 'Cumulative Amount (USD)' : `${this.selectedDataType} Amount (USD)`,
            font: { size: 12, weight: 'bold' },
            color: '#333',
            padding: { right: 10 }
          },
          position: 'left',
          ticks: {
            callback: (value: any) => {
              const thousand = (value / 1000).toFixed(0);
              if (thousand === '0') return '$0';
              return `$${thousand}K`;
            },
            font: { size: 10 },
            stepSize: stepSize,
            color: '#666',
            padding: 6,
            precision: 0
          },
          grid: {
            color: (context: any) => {
              if (context.tick && context.tick.value > 0) {
                return 'rgba(0, 0, 0, 0.04)';
              }
              return 'rgba(0, 0, 0, 0.08)';
            },
            lineWidth: isLineChart ? 1 : 0.5,
            drawBorder: true,
            drawOnChartArea: true,
            drawTicks: isLineChart
          }
        }
      },
      elements: {
        // Line-specific styling
        line: isLineChart ? {
          tension: 0.4,
          borderWidth: 3,
          borderCapStyle: 'round',
          borderJoinStyle: 'round'
        } : undefined,
        // Point styling for lines
        point: isLineChart ? {
          radius: (context: any) => {
            const value = context.dataset.data[context.dataIndex];
            return value > 0 ? 5 : 0;
          },
          hoverRadius: 7,
          hitRadius: 10
        } : undefined,
        // Bar-specific styling for cumulative
        bar: !isLineChart ? {
          borderRadius: 4,
          borderSkipped: false,
          borderWidth: 1,
          inflateAmount: 0 // No padding for stacked cumulative bars
        } : undefined
      },
      // Layout for bars
      layout: !isLineChart ? {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10
        }
      } : undefined,
      animation: {
        duration: 800,
        easing: isLineChart ? 'easeOutQuart' : 'easeOutBack', // Smooth animation for cumulative
        delay: (context: any) => {
          let delay = 0;
          if (context.type === 'data' && context.element.$index > 0) {
            delay = context.element.$index * 75; // Slightly slower stagger for cumulative effect
          }
          return delay;
        }
      },
      hover: {
        mode: isLineChart ? 'index' : 'nearest',
        intersect: false
      }
    };

    console.log(`Chart prepared - Type: ${isLineChart ? 'Line' : 'Cumulative Stacked Bar'}, Period: ${months.length} months, Workflow: $${totalWorkflowSales.toLocaleString()}, KIK#F1: $${totalKikF1Sales.toLocaleString()}`);

    // Debug data output
    console.log('Chart Data:', {
      labels: months,
      workflowCumulativeData: workflowCumulativeData,
      kikF1CumulativeData: kikF1CumulativeData,
      maxValue: yAxisMax,
      periodType: this.selectedTotalAnnualSalesYearType
    });
  }


  calculateRevenueThisMonth(): void {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-based month (0 = January)
    const currentYear = now.getFullYear();

    let sum = 0;

    // To store items that have payments in current month/year
    const itemsWithPaymentsThisMonth: any[] = [];

    this.workflowItems.forEach(item => {
      // Filter payments only for current month/year
      const matchingPayments = (item.payments || []).filter((pay: any) => {
        const paymentDate = new Date(pay.paymentReceivedDate);
        return !isNaN(paymentDate.getTime()) &&
          paymentDate.getMonth() === currentMonth &&
          paymentDate.getFullYear() === currentYear;
      });

      if (matchingPayments.length > 0) {
        // Sum paymentPrice for matching payments
        const itemSum = matchingPayments.reduce((acc, pay) => acc + (pay.paymentPrice || 0), 0);
        sum += itemSum;

        // Push the item along with its matching payments for logging
        itemsWithPaymentsThisMonth.push({
          item,
          payments: matchingPayments,
          totalPaymentThisMonth: itemSum
        });
      }
    });

    this.revenueThisMonth = parseFloat(sum.toFixed(2));
  }


  updateAnnualSalesChart(): void {
    console.log('Updating annual sales chart with selections:', {
      year: this.selectedTotalAnnualSalesYear,
      yearType: this.selectedTotalAnnualSalesYearType,
      customer: this.selectedTotalAnnualSalesCustomer,
      dataType: this.selectedDataType,
      chartType: this.annualSalesChartType
    });

    if (!this.selectedTotalAnnualSalesYear) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Year Required',
        detail: 'Please select a year to view the chart',
        life: 3000
      });
      return;
    }

    this.prepareAnnualSalesChart();
  }
  private getMonthLabelForDate(
    date: Date,
    selectedYear: number,
    yearType: string,
    months: string[]
  ): { monthLabel: string | null; isValidDate: boolean } {

    const month = date.getMonth(); // 0=Jan, 11=Dec
    const year = date.getFullYear();

    if (yearType === 'financial') {
      // Financial year: Apr-Dec of selectedYear (months 3-11), Jan-Mar of next year (0-2)
      const isInFinancialYear = (
        (month >= 3 && year === selectedYear) || // Apr-Dec selected year
        (month < 3 && year === selectedYear + 1)  // Jan-Mar next year
      );

      if (isInFinancialYear) {
        const monthIndex = month >= 3 ? month - 3 : month + 9;
        return {
          monthLabel: months[monthIndex] || null,
          isValidDate: true
        };
      }
    } else {
      // Calendar year
      if (year === selectedYear) {
        return {
          monthLabel: months[month] || null,
          isValidDate: true
        };
      }
    }

    return { monthLabel: null, isValidDate: false };
  }

  private findMonthLabelForKikF1(
    monthIndex: number,
    kikYear: number,
    selectedYear: number,
    yearType: string,
    months: string[]
  ): string | null {

    if (yearType === 'financial') {
      // Financial year mapping
      if ((monthIndex >= 3 && kikYear === selectedYear) || (monthIndex < 3 && kikYear === selectedYear + 1)) {
        const targetIndex = monthIndex >= 3 ? monthIndex - 3 : monthIndex + 9;
        return months[targetIndex] || null;
      }
    } else {
      // Calendar year
      if (kikYear === selectedYear) {
        return months[monthIndex] || null;
      }
    }

    return null;
  }

  openDueQuotationDialog(): void {
    this.ref = this.dialogService.open(DueQuotationDialogComponent, {
      width: '90vw',
      height: '80vh',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: true,
      header: '', // Empty header or remove this line
      showHeader: false, // This will hide the default header
      data: {
        workflowItems: this.workflowItems
      }
    });

    this.ref.onClose.subscribe(() => {
      // Handle dialog close if needed
    });
  }


  ngOnDestroy(): void {
    if (this.ref) {
      this.ref.close();
    }
  }
  loadKikF1Data(): void {
    this.workflowDataService.getAllKikF1Data().subscribe({
      next: (response: any) => {
        console.log('Raw KIK#F1 API Response:', response);

        // Fix: Access the nested kikF1Detail array
        const kikF1DetailArray = response?.kikF1Detail || response || [];
        console.log('Extracted kikF1Detail array:', kikF1DetailArray);
        console.log('Array length:', kikF1DetailArray.length);

        this.kikF1Data = Array.isArray(kikF1DetailArray) ? kikF1DetailArray : [];

        if (this.kikF1Data.length > 0) {
          this.processKikF1Data();
          console.log(`Successfully loaded ${this.kikF1Data.length} KIK#F1 records`);

          // Update chart if year is selected
          if (this.selectedTotalAnnualSalesYear) {
            this.updateAnnualSalesChart();
          }
        } else {
          console.warn('No KIK#F1 data found in response');
          this.messageService.add({
            severity: 'warn',
            summary: 'Data Warning',
            detail: 'KIK#F1 data received but appears empty',
            life: 4000
          });
        }
      },
      error: (error) => {
        console.error('Error loading KIK#F1 data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Data Load Error',
          detail: 'Failed to load KIK#F1 data',
          life: 5000
        });
        this.kikF1Data = [];
        this.kikF1MonthlyTotals.clear();
      }
    });
  }

  private processKikF1Data(): void {
    console.log('Starting KIK#F1 data processing...');
    console.log('Raw KIK#F1 data:', this.kikF1Data);

    this.kikF1MonthlyTotals.clear();

    if (!Array.isArray(this.kikF1Data) || this.kikF1Data.length === 0) {
      console.warn('No KIK#F1 data to process');
      return;
    }

    // Month name to index mapping
    const monthNames: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };

    let processedCount = 0;
    let skippedCount = 0;

    this.kikF1Data.forEach((item: any, index: number) => {
      console.log(`Processing item ${index}:`, item);

      // Validate item structure
      if (!item || typeof item !== 'object') {
        console.warn(`Invalid item structure at index ${index}:`, item);
        skippedCount++;
        return;
      }

      const monthName = item.month;
      const year = parseInt(item.year);
      const valueStr = item.value;

      // Validate required fields
      if (!monthName || isNaN(year) || !valueStr) {
        console.warn(`Missing required fields in item ${index}:`, {
          month: monthName,
          year: year,
          value: valueStr,
          item
        });
        skippedCount++;
        return;
      }

      const monthIndex = monthNames[monthName];
      if (monthIndex === undefined) {
        console.warn(`Invalid month name "${monthName}" in item ${index}`);
        skippedCount++;
        return;
      }

      const value = parseFloat(valueStr);
      if (isNaN(value) || value < 0) {
        console.warn(`Invalid value "${valueStr}" (parsed: ${value}) in item ${index}`);
        skippedCount++;
        return;
      }

      // Normalize customer name (use the same normalization as workflow data)
      const normalizedCustomer = this.normalizeCustomerName(item.customerName);
      const key = `${year}-${String(monthIndex).padStart(2, '0')}-${normalizedCustomer}`;

      // Add to Map
      const current = this.kikF1MonthlyTotals.get(key) || 0;
      this.kikF1MonthlyTotals.set(key, current + value);
      processedCount++;

      console.log(`Processed ${key}: $${value.toFixed(2)} (Total: $${(current + value).toFixed(2)})`);
    });

    console.log(`KIK#F1 Processing Complete:`);
    console.log(`- Processed: ${processedCount} items`);
    console.log(`- Skipped: ${skippedCount} items`);
    console.log(`- Map size: ${this.kikF1MonthlyTotals.size}`);
    console.log('Map contents:', Array.from(this.kikF1MonthlyTotals.entries()));

    // Debug: Show sample entries
    if (this.kikF1MonthlyTotals.size > 0) {
      console.log('First 3 Map entries:');
      let count = 0;
      for (const [key, value] of this.kikF1MonthlyTotals.entries()) {
        if (count >= 3) break;
        console.log(`  ${key}: $${value.toFixed(2)}`);
        count++;
      }
    }
  }
openDetailDialog(type: 'quotation' | 'po' | 'invoice' | 'revenue'): void {
  this.detailDialogType    = type;
  this.detailSearchTerm    = '';
  this.selectedDetailItem  = null;
  this.openTreeCards.clear();

  // Build item list based on type — reuse filtered sets from calculateMetrics()
  this.detailDialogItems = this.buildItemsForType(type);
  this.filteredDetailItems = [...this.detailDialogItems];

  // Summary strip + total
  this.buildSummaryStrip(type);
  this.buildFooterTotals(type);

  this.detailDialogVisible = true;
}

private buildItemsForType(type: string): any[] {
  switch (type) {
    case 'quotation':
      return [...this.filteredItems];   // already filtered by applyFilters()

    case 'po':
      return this.workflowItems.filter(item => {
        if (!item.poNumber || !item.poDate || item.poDate.trim() === '') return false;
        return this.matchesDateFilter(new Date(item.poDate), 'poDate') &&
               this.matchesCustomer(item);
      });

    case 'invoice':
      return this.workflowItems.filter(item => {
        if (!item.invoices || item.invoices.length === 0) return false;
        return this.matchesCustomer(item) &&
               item.invoices.some((inv: any) => this.matchesDateFilter(new Date(inv.date), 'invoice'));
      });

    case 'revenue':
      return this.workflowItems.filter(item => {
        if (!item.payments || item.payments.length === 0) return false;
        return this.matchesCustomer(item) &&
               item.payments.some((pay: any) =>
                 pay.paymentReceived &&
                 this.matchesDateFilter(new Date(pay.paymentReceivedDate), 'payment'));
      });

    default: return [];
  }
}

/** Shared date-range check — mirrors your calculateMetrics logic */
private matchesDateFilter(date: Date, _context: string): boolean {
  if (isNaN(date.getTime())) return false;
  let match = true;

  if (this.selectedYearType === 'calendar' && this.selectedCalendarYear) {
    match = match && date >= this.selectedCalendarYear.value.start &&
                     date <= this.selectedCalendarYear.value.end;
  } else if (this.selectedYearType === 'financial' && this.selectedFinancialYear) {
    match = match && date >= this.selectedFinancialYear.value.start &&
                     date <= this.selectedFinancialYear.value.end;
  }

  if (this.selectedMonth) {
    match = match && (date.getMonth() + 1 === Number(this.selectedMonth));
  }
  return match;
}

private matchesCustomer(item: any): boolean {
  if (!this.selectedCustomer) return true;
  const sel = this.normalizeCustomerName(this.selectedCustomer).toLowerCase();
  if (sel === 'all') return true;
  return this.normalizeCustomerName(item.customerName).toLowerCase() === sel;
}

private buildSummaryStrip(type: string): void {
  const items = this.detailDialogItems;
  const totalQuote    = items.reduce((s: number, i: any) => s + (i.totalPrice || 0), 0);
  const totalInvAmt   = items.reduce((s: number, i: any) => s + this.calcTotalInvoices(i), 0);
  const totalPaidAmt  = items.reduce((s: number, i: any) => s + this.calcTotalPayments(i), 0);
  const outstanding   = totalInvAmt - totalPaidAmt;

  this.detailDialogTotalAmount = type === 'revenue' ? totalPaidAmt :
                                  type === 'invoice' ? totalInvAmt : totalQuote;

  this.detailSummaryStrip = [
    { label: 'Records',     value: items.length,    formatted: false, color: 'primary' },
    { label: 'Quote Value', value: totalQuote,       formatted: true,  color: 'primary' },
    { label: 'Invoiced',    value: totalInvAmt,      formatted: true,  color: 'info' },
    { label: 'Received',    value: totalPaidAmt,     formatted: true,  color: 'success' },
    { label: 'Outstanding', value: outstanding < 0 ? 0 : outstanding, formatted: true, color: outstanding > 0 ? 'warning' : 'success' },
  ];
}

private buildFooterTotals(type: string): void {
  const items = this.detailDialogItems;
  this.detailFooterTotals = [
    { label: 'Total Records', value: items.length, formatted: false, color: '' },
    { label: 'Total Quote',   value: items.reduce((s: number, i: any) => s + (i.totalPrice || 0), 0), formatted: true, color: 'primary' },
    { label: 'Total Invoiced',value: items.reduce((s: number, i: any) => s + this.calcTotalInvoices(i), 0), formatted: true, color: 'info' },
    { label: 'Total Received',value: items.reduce((s: number, i: any) => s + this.calcTotalPayments(i), 0), formatted: true, color: 'success' },
  ];
}

// ── Item list helpers ─────────────────────────────────────────
getItemPrimaryLabel(item: any): string {
  switch (this.detailDialogType) {
    case 'po':      return item.poNumber     || item.quotationNo;
    case 'invoice': return item.invoices?.[0]?.invoiceNo || item.quotationNo;
    default:        return item.quotationNo;
  }
}

getItemDateLabel(item: any): string {
  const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '';
  switch (this.detailDialogType) {
    case 'po':      return fmt(item.poDate);
    case 'invoice': return fmt(item.invoices?.[0]?.date);
    case 'revenue': return fmt(item.payments?.find((p: any) => p.paymentReceived)?.paymentReceivedDate);
    default:        return fmt(item.quotationDate);
  }
}

getItemAmount(item: any): number {
  switch (this.detailDialogType) {
    case 'invoice': return this.calcTotalInvoices(item);
    case 'revenue': return this.calcTotalPayments(item);
    default:        return item.totalPrice || 0;
  }
}

getItemBadgeClass(item: any): string {
  const inv   = this.calcTotalInvoices(item);
  const paid  = this.calcTotalPayments(item);

  if (this.detailDialogType === 'revenue') {
    return paid > 0 ? 'dd-list-item__badge--paid' : 'dd-list-item__badge--none';
  }
  if (this.detailDialogType === 'invoice') {
    if (inv === 0)    return 'dd-list-item__badge--none';
    if (paid >= inv)  return 'dd-list-item__badge--paid';
    if (paid > 0)     return 'dd-list-item__badge--partial';
    return 'dd-list-item__badge--pending';
  }
  if (this.detailDialogType === 'po') {
    return item.poNumber ? 'dd-list-item__badge--paid' : 'dd-list-item__badge--none';
  }
  // quotation
  if (paid >= (item.totalPrice || 0) && paid > 0) return 'dd-list-item__badge--paid';
  if (paid > 0)  return 'dd-list-item__badge--partial';
  return 'dd-list-item__badge--pending';
}

getItemBadgeLabel(item: any): string {
  const cls = this.getItemBadgeClass(item);
  return cls.includes('paid')    ? 'Paid'
       : cls.includes('partial') ? 'Partial'
       : cls.includes('pending') ? 'Pending'
       : 'No Data';
}

// ── Tree card open/close ──────────────────────────────────────
toggleTreeCard(key: string): void {
  this.openTreeCards.has(key) ? this.openTreeCards.delete(key) : this.openTreeCards.add(key);
}

isTreeCardOpen(key: string): boolean {
  return this.openTreeCards.has(key);
}

// ── Detail select / search ────────────────────────────────────
selectDetailItem(item: any): void {
  this.selectedDetailItem = item;
  this.openTreeCards.clear();   // collapse all on new selection
}

filterDetailItems(): void {
  const term = this.detailSearchTerm.toLowerCase().trim();
  if (!term) { this.filteredDetailItems = [...this.detailDialogItems]; return; }
  this.filteredDetailItems = this.detailDialogItems.filter(item =>
    (item.quotationNo   || '').toLowerCase().includes(term) ||
    (item.customerName  || '').toLowerCase().includes(term) ||
    (item.poNumber      || '').toLowerCase().includes(term) ||
    (item.jobNumber     || '').toLowerCase().includes(term) ||
    (item.project       || '').toLowerCase().includes(term) ||
    (item.invoices || []).some((inv: any) => (inv.invoiceNo || '').toLowerCase().includes(term))
  );
}

onDetailDialogHide(): void {
  this.selectedDetailItem = null;
  this.openTreeCards.clear();
  this.detailSearchTerm = '';
}

// ── Calculation helpers ───────────────────────────────────────
calcTotalInvoices(item: any): number {
  return (item.invoices || []).reduce((s: number, inv: any) => s + (inv.invoicePrice || 0), 0);
}

calcTotalPayments(item: any): number {
  return (item.payments || [])
    .filter((p: any) => p.paymentReceived)
    .reduce((s: number, p: any) => s + (p.paymentPrice || 0), 0);
}

calcProgressPct(paid: number, total: number): number {
  if (!total || total === 0) return 0;
  return Math.min(100, Math.round((paid / total) * 100));
}

getProgressClass(paid: number, total: number): string {
  const pct = this.calcProgressPct(paid, total);
  if (pct === 0)   return 'dd-tree-card__progress-fill--zero';
  if (pct < 100)   return 'dd-tree-card__progress-fill--partial';
  return '';
}

getPaymentForInvoice(item: any, invoiceNo: string): any | null {
  return (item.payments || []).find((p: any) => p.invoiceNo === invoiceNo) || null;
}

isOverdue(dateStr: string): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

// ── Workflow stepper ──────────────────────────────────────────
isStepDone(item: any, stepKey: string): boolean {
  const currentIdx = this.stepOrder.indexOf(item.currentStep);
  const stepIdx    = this.stepOrder.indexOf(stepKey);
  if (item.completed) return true;
  return stepIdx < currentIdx;
}

getWorkflowStepTagClass(step: string): string {
  const map: Record<string, string> = {
    quotation: 'dd-tag--primary',
    po:        'dd-tag--info',
    pi:        'dd-tag--info',
    workOrder: 'dd-tag--warning',
    ci:        'dd-tag--warning',
    payment:   'dd-tag--success',
  };
  return map[step] || 'dd-tag--neutral';
}
}